// Typed client for the SageLeaf API.
//
// From M0 slice 5 these types are generated from api/openapi.yaml rather than
// written by hand; the shapes below match the spec exactly so that swap is a
// deletion, not a rewrite.

import { withFirstTouch } from "./firstTouch";

/** What reading a book needs from this viewer (ADR-0197). */
export type Access = "open" | "sign_in" | "plus";

export interface Book {
  id: string;
  org_id: string;
  /** The canonical link, built by the server (ADR-0209, ADR-0210): copy it, never build one. */
  url?: string;
  /**
   * Whether a reader is offered Share for this book: the server's link-preview
   * rule (ADR-0209 §3). Never re-derived from visibility and publication.
   */
  shareable?: boolean;
  slug: string;
  title: string;
  /** The author line, built from the author links (ADR-0185). */
  author?: string;
  /**
   * Every writer the book names: authors, then translators, each in order
   * (requirement 0118). Absent from an older server.
   */
  writers?: BookWriter[];
  /**
   * The book's tags (requirement 0119 T3), each a Browse filter. Absent from
   * an older server.
   */
  tags?: Tag[];
  description?: string;
  language: string;
  /**
   * Whether somebody without an account may read this book, and what the
   * public gallery lists (ADR-0070). One flag for both: a flag that only
   * filtered the gallery would be a setting that lies on a shared link.
   */
  free: boolean;
  /** A signed link to the book's cover, absent when it has none (ADR-0041). */
  cover_url?: string;
  cover_width?: number;
  cover_height?: number;
  /** How long the chapters a reader is served play in total; 0 with no audio. */
  listen_ms?: number;
  /**
   * Whether this viewer keeps the book on their own shelf (ADR-0060).
   *
   * The server's answer, over the page it just sent. Never worked out here by
   * intersecting the shelf with the catalogue: that is wrong on every page but
   * the first, and it is wrong for as long as the second list is in flight.
   */
  in_library?: boolean;
  /**
   * How many chapters a reader is served, and how many of them this viewer has
   * finished (ADR-0061). The tile's progress bar is the second over the first.
   *
   * `chapters_finished` is absent for a signed-out visitor — not zero, which
   * would say they started the book and stopped.
   */
  chapters_total?: number;
  chapters_finished?: number;
  /**
   * When this viewer finished the last of the book's chapters, sent only once
   * every one is: the month on the shelf's Finished seal (owner, 10-22).
   */
  finished_at?: string;
  /**
   * The reading-room plate the app draws the book in (ADR-0194), on the
   * single-book read only. The web has no room; it is here because the DTO is
   * shared.
   */
  room_plate?: string;
  visibility: "private" | "unlisted" | "public" | "shared";
  /**
   * Whether this viewer reads the book because it was shared with a group
   * they are in (ADR-0153). The server's answer, from authz.
   */
  shared_with_you?: boolean;
  /**
   * What reading this book needs from this viewer (ADR-0197): the server's
   * answer from authz, on every reader's tile and book page. "open" reads now;
   * "sign_in" opens after signing in; "plus" needs Plus. Draw the badge from
   * it; never work it out. Absent on the studio's own views.
   */
  access?: Access;
  /** access other than "open"; kept for the apps already shipped. */
  locked?: boolean;
  /** What the published edition holds (ADR-0197): the face's counts. */
  pictures_total?: number;
  portraits_total?: number;
  /**
   * Where the book sits in its series for this viewer (requirement 0119 T2),
   * counted over the volumes they may read. Absent when it is in none.
   */
  series?: SeriesPlace;
  /**
   * The groups the book is shared with, on its own library's views only (the
   * studio). Absent means none; kept while the book is not shared, and in
   * force again when it is.
   */
  shared_with?: string[];
  published: boolean;
  published_at?: string;
  updated_at: string;
  /**
   * The book this one's content is derived from, and the transform that fills
   * it, both absent unless the book is linked (requirement 0038, ADR-0093).
   *
   * A linked book's words, cast and pictures come from its source and are never
   * edited by hand — the server refuses every one of those writes with a 409, so
   * leaving the controls out here is showing the rule rather than keeping it.
   */
  linked_source_id?: string;
  link_kind?: string;
  /**
   * The voice this book is narrated in. Absent means the book never chose one
   * and takes the installation's default — which is a different statement from
   * having chosen that voice, and the studio says so.
   */
  default_voice?: string;
  /** The speech model the book is narrated in; absent means the default (ADR-0028). */
  narration_model?: string;
  /** Characters per narrated part; absent means the default, 350 (ADR-0029). */
  chunk_chars?: number;
  /** Whether each part is recorded from the sentence before it, which is then cut out (ADR-0030). */
  read_context?: boolean;
  /**
   * How this book is to be read, in words — "unhurried, like an audiobook
   * narrator" (ADR-0109). Absent means undirected.
   *
   * Sent whatever the book's model is, so a direction is not lost by
   * auditioning a model that takes none; the studio hides the control instead,
   * and the server refuses the field on such a model.
   */
  narration_direction?: string;
  /**
   * Narrated with a voice for every character, from each chapter's
   * performance script (ADR-0157). Only on an English book whose model
   * performs; the server refuses it anywhere else.
   */
  multi_speaker?: boolean;
  /**
   * Whether the book tells a story (ADR-0158). The author sets it; the Ask
   * sheet's hint follows it. Absent from an older server means a story.
   */
  story?: boolean;
  /** The edition readers are served; absent when the book is not public. */
  edition?: EditionRef;
  /** What needs doing, on the studio's own list only. */
  studio?: StudioSummary;
}

/** StudioSummary is the state of the work on a book, for the studio's list. */
export interface StudioSummary {
  chapters: number;
  narrated: number;
  audio_outdated: number;
  narrating: number;
  changes_to_publish: number;
  /** The run writing this book, when it was started as a translation (ADR-0055). */
  translation?: TranslationState;
}

/** TranslationStatus is where a translation run stands (ADR-0055). */
export type TranslationStatus = "running" | "paused" | "done" | "cancelled";

/** TranslationState is a run's headline, on the studio's list. */
export interface TranslationState {
  status: TranslationStatus;
  chapters_done: number;
  chapters_total: number;
}

/**
 * Translation is a translated book's run. `reason` is a stable code the client
 * words: refused, failed, budget, too_long, invalid, source_gone.
 */
export interface Translation extends TranslationState {
  reason?: string;
  language: string;
  source_book_id: string;
  source_title: string;
  /** 1-based chapter the run paused on; 0 when it paused before any chapter. */
  failed_chapter: number;
  spent_micros: number;
  spent: string;
  /** Pictures put in place by proportion alone, where the text did not say. */
  estimated_pictures: { chapter_id: string; chapter_title: string; picture_ids: string[] }[];
  /**
   * How this book is translated, and the ready-made instruction it came from
   * while it is still that preset's words (ADR-0094). Absent means the default,
   * which the style endpoint supplies.
   */
  instruction?: string;
  preset?: string;
  /**
   * What the current pass is thinking at (ADR-0097). Recorded even when it was
   * defaulted, so the studio seeds its chip with what this book last ran at
   * rather than with a guess.
   */
  effort?: Effort;
  /**
   * How many paragraphs a refresh kept the author's wording for, with an
   * alternative it bought and did not write (ADR-0098 §5). A count of
   * unreviewed items is what the book *is*, so it rides on the status beside
   * the banner rather than on the refresh preview.
   */
  open_conflicts?: number;
}

/**
 * Effort is how hard the model is asked to think about a translation
 * (ADR-0097). The set comes from the server, never a copy here: a client
 * offering a fourth would spend at a level the server then refuses.
 */
export type Effort = "fast" | "medium" | "high";

/** TranslationEstimate is what translating a book would cost, formatted by the server. */
export interface TranslationEstimate {
  chapters: number;
  estimate_micros: number;
  estimate: string;
}

/** EditionRef is a published edition as a book carries it (ADR-0024). */
export interface EditionRef {
  number: number;
  published_at: string;
  note?: string;
}

/** EditionChange is what publishing now would do to one chapter. */
export interface EditionChange {
  chapter_id: string;
  title: string;
  kind: "added" | "removed" | "changed";
  renamed: boolean;
  moved: boolean;
  text: boolean;
  audio: boolean;
  /** The chapter would be published with no audio at all. */
  no_audio: boolean;
  /** …or with audio recorded from an earlier revision of its text. */
  audio_outdated: boolean;
  /**
   * What publishing would do to this chapter's pictures (ADR-0062). Absent
   * means none of that kind. A chapter whose only change is a picture is in
   * the list for that alone.
   */
  pictures_added?: number;
  pictures_removed?: number;
  pictures_changed?: number;
}

export interface EditionPlan {
  published: EditionRef | null;
  next_number: number;
  changes: EditionChange[];
  /** The cast differs from what readers have (ADR-0034). */
  characters_changed: boolean;
  /**
   * The cover differs from the one readers were published — set, replaced or
   * cleared since the last edition (ADR-0062). A cover is a draft until it is
   * published, like everything else a reader is served.
   */
  cover_changed: boolean;
  /** Quotes whose speaker differs from what readers are served (ADR-0126). */
  speakers_changed: number;
  /**
   * Lines readers have a speaker for that publishing now would serve with
   * none (ADR-0195): an edit the studio could not follow. A warning — Update
   * in who says what gives them one.
   */
  speakers_lost?: number;
}

/** getEditionPlan is what publishing now would change. Studio only. */
export function getEditionPlan(bookID: string): Promise<EditionPlan> {
  return request<EditionPlan>(`/api/v1/books/${bookID}/edition`);
}

/**
 * Which copy of a book to read (ADR-0024). Readers get the published edition
 * by default; the studio asks for the working copy with `draft`.
 */
interface ViewOptions {
  /**
   * The book page's own fetch of the book, the one counted as a page view
   * (ADR-0190 §5): the reader's and the chat's fetches of the same book are
   * not. Only getBook reads it.
   */
  screen?: "book";
  draft?: boolean;
}

const viewQuery = (opts: ViewOptions) => (opts.draft ? "?draft=true" : "");

export interface BookList {
  items: Book[];
  next_cursor?: string;
}

/** Org is one tenant the signed-in user belongs to. */
export interface Org {
  id: string;
  slug: string;
  name: string;
  personal: boolean;
  role: string;
  /**
   * The permissions this user holds in this org. The UI decides what to render
   * from these, never from `role` — a custom role would make any mapping from
   * role name to capability disagree with the server.
   */
  permissions: string[];
}

export interface User {
  id: string;
  email: string;
  name?: string;
  image_url?: string;
  /** The interface language (ADR-0023). */
  locale: "en" | "fa";
  platform_admin: boolean;
  /** Whether the account holds Plus (ADR-0193). Absent from an older server. */
  plus?: boolean;
}

/**
 * updateMyLocale changes the signed-in account's interface language and returns
 * the session as it now stands.
 */
export function updateMyLocale(locale: "en" | "fa"): Promise<Me> {
  return request<Me>("/api/v1/me", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locale }),
  });
}

export interface Me {
  user: User;
  orgs: Org[];
}

/** Problem is the RFC 9457 error body every endpoint returns on failure. */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code: string;
  request_id?: string;
}

/** ApiError carries the problem document so callers can switch on `code`. */
export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly status: number,
  ) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
  }
}

/**
 * readCookie returns a cookie value, or undefined.
 *
 * Only the CSRF cookie is read this way; the session cookie is httpOnly and is
 * deliberately invisible to script.
 */
function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return undefined;
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };

  // Double-submit: the server compares this header against the cookie it set.
  // Bearer clients (mobile) are exempt, because nothing attaches an
  // Authorization header automatically.
  if (UNSAFE.has(method)) {
    const csrf = readCookie("sl_csrf");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  const res = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
  });

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    // A failure that is not a problem document is a bug or an outage upstream;
    // surface it as one rather than letting a JSON parse error stand in for it.
    let problem: Problem;
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = {
        type: "about:blank",
        title: "Unexpected error",
        status: res.status,
        code: "internal",
        detail: `The server returned ${res.status} with no error detail.`,
      };
    }
    throw new ApiError(problem, res.status);
  }

  return (await res.json()) as T;
}

/** Browse's listing; a tag narrows it to the books carrying it (0119 T3). */
export function listBooks(cursor?: string, tag?: string, limit?: number): Promise<BookList> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (tag) params.set("tag", tag);
  if (limit) params.set("limit", String(limit));
  const query = params.toString();
  return request<BookList>(`/api/v1/books${query ? `?${query}` : ""}`);
}

export function listOrgBooks(org: string, cursor?: string): Promise<BookList> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return request<BookList>(
    `/api/v1/orgs/${encodeURIComponent(org)}/books${query ? `?${query}` : ""}`,
  );
}

export function createBook(
  org: string,
  /** `language` is chosen with the title (ADR-0069): every paid call about the
      book is written in it, and a book that has not said which is refused. */
  body: { title: string; author?: string; language?: string },
): Promise<Book> {
  return request<Book>(`/api/v1/orgs/${encodeURIComponent(org)}/books`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * updateBook edits a book's metadata. Fields are optional and only the ones
 * present are changed — the server takes pointers, so omitting a field and
 * clearing it are different requests.
 */
export function updateBook(
  id: string,
  patch: {
    title?: string;
    /**
     * Every writer the book names, in order within each role (0118 W2): an
     * existing one by id, or a name found or created. The author line is built
     * from these; there is no `author` to send.
     */
    writers?: WriterChoice[];
    /** The book's tags (0119 T3): an existing one by id, or a name found or made. */
    tags?: TagChoice[];
    description?: string;
    /** The language the book is written in; "" means not chosen. */
    language?: string;
    slug?: string;
    default_voice?: string;
    narration_model?: string;
    /** The model that says who speaks each line; "" is the house default (ADR-0181). */
    label_model?: string;
    chunk_chars?: number;
    read_context?: boolean;
    /** How the book is to be read; "" clears it (ADR-0109). */
    narration_direction?: string;
    /** A voice for every character (ADR-0157). */
    multi_speaker?: boolean;
    /** The accent the book's voices speak in, one of the cast's `accents` (ADR-0164). */
    voice_accent?: string;
    /** Offers the book to people without an account (ADR-0070). */
    free?: boolean;
    /** Whether the book tells a story (ADR-0158). */
    story?: boolean;
  },
): Promise<Book> {
  return request<Book>(`/api/v1/books/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

/**
 * Who may read a published book: everyone, the owner only (ADR-0152), or the
 * library's chosen groups (ADR-0153).
 */
export type ReadableBy = "public" | "private" | "shared";

/**
 * publishBook releases the working copy as a new edition, with an optional
 * note for readers, or takes the book away from readers. Publishing a book
 * readers already have in full creates nothing.
 *
 * `visibility` absent keeps the book's current one, the first publish included
 * (ADR-0152): a new book is private until a publish names public.
 */
export function publishBook(
  id: string,
  published: boolean,
  note = "",
  visibility?: ReadableBy,
  groupIDs?: string[],
): Promise<Book> {
  if (!published) {
    return request<Book>(`/api/v1/books/${id}/publish`, { method: "DELETE" });
  }
  return request<Book>(`/api/v1/books/${id}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      note,
      ...(visibility ? { visibility } : {}),
      ...(groupIDs ? { group_ids: groupIDs } : {}),
    }),
  });
}

/**
 * setBookVisibility changes who may read a published book without a new
 * edition (ADR-0152). Making a book public needs an edition.
 */
export function setBookVisibility(
  id: string,
  visibility: ReadableBy,
  groupIDs?: string[],
): Promise<Book> {
  return request<Book>(`/api/v1/books/${id}/visibility`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    // group_ids, when sent, replaces the groups before the visibility changes.
    body: JSON.stringify(groupIDs ? { visibility, group_ids: groupIDs } : { visibility }),
  });
}

/**
 * A library's group: a named list of people a published book can be shared
 * with (ADR-0153). It owns nothing and spends nothing.
 */
export interface Group {
  id: string;
  name: string;
  people: GroupPerson[];
}

export interface GroupPerson {
  user_id: string;
  email: string;
  name?: string;
  image_url?: string;
}

export function listGroups(orgSlug: string): Promise<{ items: Group[] }> {
  return request<{ items: Group[] }>(`/api/v1/orgs/${orgSlug}/groups`);
}

export function createGroup(orgSlug: string, name: string): Promise<Group> {
  return request<Group>(`/api/v1/orgs/${orgSlug}/groups`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}

export function renameGroup(id: string, name: string): Promise<Group> {
  return request<Group>(`/api/v1/groups/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}

export function deleteGroup(id: string): Promise<void> {
  return request<void>(`/api/v1/groups/${id}`, { method: "DELETE" });
}

/** addToGroup adds the account that signed in with this address. */
export function addToGroup(id: string, email: string): Promise<Group> {
  return request<Group>(`/api/v1/groups/${id}/people`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export function removeFromGroup(id: string, userID: string): Promise<Group> {
  return request<Group>(`/api/v1/groups/${id}/people/${userID}`, { method: "DELETE" });
}

export function deleteBook(id: string): Promise<void> {
  return request<void>(`/api/v1/books/${id}`, { method: "DELETE" });
}

/**
 * fetchMe returns the signed-in user, or null when there is no session.
 *
 * A 401 here is the normal state for a visitor, not an error: the library is
 * public. Turning it into null keeps every caller from special-casing it.
 */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await request<Me>("/api/v1/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function signOut(): Promise<void> {
  return request<void>("/api/v1/auth/session", { method: "DELETE" });
}

/**
 * deleteAccount deletes the signed-in account and signs it out (requirement
 * 0062). What goes and what stays is the server's (ADR-0128).
 */
export function deleteAccount(): Promise<void> {
  return request<void>("/api/v1/me", { method: "DELETE" });
}

/** signInURL sends the browser to the provider and back to `returnTo`. */
export function signInURL(returnTo: string = window.location.pathname): string {
  // The first touch the browser holds, so the account keeps where the visitor
  // came from (ADR-0190 §3); carryFirstTouchOnSignIn covers a link drawn
  // before the beacon answered.
  return withFirstTouch(`/api/v1/auth/google/start?redirect_to=${encodeURIComponent(returnTo)}`);
}

/** can reports whether an org membership holds a permission. */
export function can(org: Org | undefined, permission: string): boolean {
  return org?.permissions.includes(permission) ?? false;
}

/** UsageLine is spend grouped by what it bought. Labels come from the server. */
export interface UsageLine {
  /** Stable name for what was bought, which the client translates. */
  key: string;
  /** The English name, for a key this client does not know. */
  label: string;
  count: number;
  cost_micros: number;
  cost_usd: string;
}

/** UsageCharge is one charge on the ledger. */
export interface UsageCharge {
  at: string;
  key: string;
  label: string;
  model: string;
  cost_micros: number;
  cost_usd: string;
}

/**
 * Usage is this month's spend, against the budget of your own library.
 *
 * `spent_*`, `limit_*` and `remaining_*` are the library's position — the
 * numbers a spend is refused on. `mine_*`, `by_what` and `recent` are your own
 * charges within it. Money arrives formatted, so "<$0.01" reads the same
 * everywhere.
 */
export interface Usage {
  period_start: string;
  limit_micros: number;
  limit_usd: string;
  spent_micros: number;
  spent_usd: string;
  remaining_micros: number;
  remaining_usd: string;
  mine_micros: number;
  mine_usd: string;
  by_what: UsageLine[];
  recent: UsageCharge[];
}

/**
 * SavedQuote is one of the reader's kept passages (requirement 0137,
 * ADR-0208), as GET /me/saved-quotes lists it, newest first.
 */
export interface SavedQuote {
  id: string;
  text: string;
  created_at: string;
  book_id: string;
  chapter_id: string;
  book_title: string;
  author?: string;
  chapter_title?: string;
  /** The book's language when the quote was kept: the card's direction. */
  lang?: string;
  /** Whether the reader can still open it; a closed one has no cover. */
  open: boolean;
  /** The edition's cover, signed; only on an open row. */
  cover?: string;
  /** The book's canonical link, built by the server (ADR-0209 §3); clients never build one. */
  book_url?: string;
}

export function fetchSavedQuotes(): Promise<{ items: SavedQuote[] }> {
  return request("/api/v1/me/saved-quotes");
}

export function fetchMyUsage(): Promise<Usage> {
  return request<Usage>("/api/v1/me/usage");
}

/** The roles a platform admin can give an account in its own library. */
export const ROLES = ["reader", "creator", "admin"] as const;

/** AdminUser is one account as the admin panel sees it. */
export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  image_url?: string;
  platform_admin: boolean;
  /** Switched off by an admin (ADR-0145): no session works and sign-in is refused. */
  disabled: boolean;
  /** The way in: "google", "apple", "tester", or "" for an account not yet keyed. */
  sign_in: string;
  /** The role in the account's own library, or "" when it has none. */
  role: string;
  locale: "en" | "fa";
  last_seen_at?: string;
  created_at: string;
  /** This month's spend in the account's own library. */
  spent_micros: number;
  spent_usd: string;
  /**
   * The ceiling that spend is checked against — the account's own library's
   * monthly limit (ADR-0082) — and what is left of it.
   *
   * **Absent is not zero.** An account with no library has no limit and no
   * control; zero is a limit somebody set, and it refuses the account's next
   * paid call. The two must not render alike.
   */
  limit_micros?: number;
  limit_usd?: string;
  remaining_micros?: number;
  remaining_usd?: string;
}

/** listUsers is every account. Platform admin only. */
/** One day's arrivals from one source (ADR-0129). Totals only: no visitor is listed. */
/** A count beside what it is a share of (ADR-0190). */
export interface TelemetryRatio {
  count: number;
  of: number;
  pct: number;
}

/** One day × source × campaign of the launch funnel (ADR-0190). */
export interface FunnelRow {
  day: string;
  source: string;
  campaign: string;
  visitors: number;
  returned_within_7_days: TelemetryRatio;
  signups: TelemetryRatio;
  signups_who_opened_a_book: TelemetryRatio;
  /** null until the window has ended. */
  active_days_2_7: TelemetryRatio | null;
  active_days_23_30: TelemetryRatio | null;
}

/** The funnel over the last 30 UTC days; `sl api` reads the rest. */
export function fetchFunnel(): Promise<{ items: FunnelRow[] }> {
  return request<{ items: FunnelRow[] }>("/api/v1/admin/telemetry/funnel");
}

/** One sign-up week × source (ADR-0190), with its readers' median session (0146). */
export interface CohortRow {
  week: string;
  week_start: string;
  source: string;
  signups: number;
  opened_a_book: TelemetryRatio;
  asked_a_question_of_opened: TelemetryRatio;
  active_days_2_7: TelemetryRatio | null;
  active_days_23_30: TelemetryRatio | null;
  median_week_1_minutes: number | null;
  /** null when the cohort's readers have no session. */
  median_session_seconds: number | null;
}

/** The sign-up cohorts over the default range; `sl api` reads the rest. */
export function fetchCohorts(): Promise<{ items: CohortRow[] }> {
  return request<{ items: CohortRow[] }>("/api/v1/admin/telemetry/cohorts");
}

/** One server failure, as Admin → Errors lists it (ADR-0139). */
export interface ErrorEvent {
  at: string;
  request_id: string;
  method: string;
  route: string;
  status: number;
  code: string;
  error: string;
  user_id?: string;
}

export function fetchErrors(): Promise<{ items: ErrorEvent[] }> {
  return request<{ items: ErrorEvent[] }>("/api/v1/admin/errors");
}

/** A rented machine that is billing, or stopped in the last day (ADR-0133). */
export interface ActivityMachine {
  id: string;
  lane: string;
  status: "starting" | "running" | "stopped";
  started_at: string;
  last_seen_at: string | null;
  stopped_at: string | null;
  stop_reason: string;
  up_seconds: number;
  cost_micros: number;
  /** Formatted by the server, like every amount of money. */
  cost: string;
  /** The machine's price an hour, formatted. */
  rate: string;
  running_jobs: number;
}

/** The queue's work for one book, of one kind, in one lane. */
export interface ActivityWork {
  /** Null for work with no book, or whose book is gone. */
  book_id: string | null;
  book_title: string;
  kind: string;
  lane: string;
  running: number;
  ready: number;
  /** Failed for good in the last day. */
  failed: number;
  oldest_ready_since: string | null;
  last_error: string;
}

export interface Activity {
  now: string;
  /** In English: written by the server for the owner (ADR-0133). */
  attention: string[];
  machines: ActivityMachine[];
  work: ActivityWork[];
}

/** fetchActivity is what is running now. Platform admin only (requirement 0065). */
export function fetchActivity(): Promise<Activity> {
  return request<Activity>("/api/v1/admin/activity");
}

export function listUsers(): Promise<{ items: AdminUser[] }> {
  return request<{ items: AdminUser[] }>("/api/v1/admin/users");
}

/** UserReading is one account's records (ADR-0146): time per book, questions by kind. */
export interface UserReading {
  books: { book_id: string; title: string; seconds: number }[];
  /** typed, meaning, explain, simpler — all time. */
  questions: Record<string, number>;
  /** Reading sessions (ADR-0218): overall, and per book. */
  sessions: { overall: SessionStats; books: SessionStats[] };
}

/** A count of reading sessions, their median and longest length, and how they ended. */
export interface SessionStats {
  /** Set on a per-book row; absent on the overall one. */
  book_id?: string;
  title?: string;
  count: number;
  median_seconds: number;
  longest_seconds: number;
  /** switched_away, left, idle. */
  ended_by: Record<string, number>;
}

export function fetchUserReading(userID: string): Promise<UserReading> {
  return request<UserReading>(`/api/v1/admin/users/${userID}/reading`);
}

/** setUserDisabled switches an account off or back on (ADR-0145). Platform admin only. */
export function setUserDisabled(userID: string, disabled: boolean): Promise<AdminUser> {
  return request<AdminUser>(`/api/v1/admin/users/${userID}/disabled`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ disabled }),
  });
}

/** createTester makes a tester login; the password comes back this once (ADR-0145). */
export function createTester(username: string): Promise<{ user: AdminUser; password: string }> {
  return request<{ user: AdminUser; password: string }>("/api/v1/admin/testers", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username }),
  });
}

/** setUserRole changes an account's role in its own library. Platform admin only. */
export function setUserRole(userID: string, role: string): Promise<AdminUser> {
  return request<AdminUser>(`/api/v1/admin/users/${userID}/role`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
}

/** setUserLocale changes an account's interface language. Platform admin only. */
export function setUserLocale(userID: string, locale: "en" | "fa"): Promise<AdminUser> {
  return request<AdminUser>(`/api/v1/admin/users/${userID}/locale`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locale }),
  });
}

/**
 * setUserBudget moves the ceiling an account spends against: the monthly limit
 * of its own library (ADR-0082). Platform admin only.
 *
 * Zero is a limit and stops the account; only a negative is refused. Micros on
 * the wire, dollars on the screen.
 */
export function setUserBudget(userID: string, limitMicros: number): Promise<AdminUser> {
  return request<AdminUser>(`/api/v1/admin/users/${userID}/budget`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ limit_micros: limitMicros }),
  });
}

/** DeviceGrant is a command-line sign-in waiting for an answer (ADR-0142). */
export interface DeviceGrant {
  user_code: string;
  /** What the CLI calls itself. Untrusted text. */
  label: string;
  requested_at: string;
}

/** fetchDeviceGrant is the sign-in a user code names. Platform admin, web session only. */
export function fetchDeviceGrant(code: string): Promise<DeviceGrant> {
  return request<DeviceGrant>(`/api/v1/auth/device/approve?code=${encodeURIComponent(code)}`);
}

/** decideDeviceGrant approves or denies a command-line sign-in. */
export function decideDeviceGrant(code: string, approve: boolean): Promise<void> {
  return request<void>("/api/v1/auth/device/approve", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, approve }),
  });
}

/** fetchUserUsage is another account's usage. Platform admin only. */
export function fetchUserUsage(userID: string): Promise<Usage> {
  return request<Usage>(`/api/v1/admin/users/${userID}/usage`);
}

/** Chapter is one unit of text, and from M2 one unit of audio. */
export interface Chapter {
  id: string;
  book_id: string;
  position: number;
  title: string;
  revision_id?: string;
  revision_no?: number;
  char_count: number;
  content_hash?: string;
  has_active_render: boolean;
  /** The audio was recorded from an earlier text than the chapter has now. */
  audio_outdated: boolean;
  /** A render is pending or running. */
  narrating: boolean;
  /** How long the chapter's audio plays; absent when it has none. */
  duration_ms?: number;
  /** Whether readers have this chapter as it is; draft view only (ADR-0024). */
  edition_state?: "unpublished" | "outdated" | "current";
  /** Passages of the current narration with no audio; studio lists only (ADR-0120). */
  missing_passages?: number;
  /** No part of the current narration has audio. */
  narration_failed?: boolean;
  updated_at: string;
}

export interface ChapterText extends Chapter {
  body: string;
  /**
   * The body's italics (ADR-0205), as byte spans `[from, to)` of it — the unit
   * speakers and words are placed in. Optional for a rolling deploy's old
   * server. A display fact: nothing spoken depends on it.
   */
  emphasis?: Spans;
}

/** Byte spans `[from, to)` of a revision's body: where emphasis falls. */
export type Spans = [number, number][];

/**
 * Emphasis as an import reads it (ADR-0205): runes `[start, end)` of line
 * `line` of the body. Opaque to the client: it goes back with the chapter.
 */
export interface EmphasisRange {
  line: number;
  start: number;
  end: number;
}

/**
 * getBook reads a book as one of its two bodies (ADR-0024, ADR-0062).
 *
 * `draft` is the studio looking at its own work, and is what makes `cover_url`
 * the cover the author has set rather than the one readers were published. It
 * takes the same permission a draft chapter does, so it is the studio's to ask
 * for; reader screens leave it off. Give a draft read its own query key —
 * `["book", id, "draft"]` — or the two bodies share one cache entry.
 */
export function getBook(id: string, opts: ViewOptions = {}): Promise<Book> {
  const query = opts.screen === "book" && !opts.draft ? "?screen=book" : viewQuery(opts);
  return request<Book>(`/api/v1/books/${id}${query}`);
}

/**
 * addReadingTime adds seconds read and listened in a book to today
 * (ADR-0146, ADR-0190 §6). keepalive, so the last increment of a page being
 * left still reaches the server.
 */
export function addReadingTime(bookID: string, read: number, listen: number): Promise<void> {
  return request<void>(`/api/v1/books/${bookID}/reading-time`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ read_seconds: read, listen_seconds: listen }),
    keepalive: true,
  });
}

/** A writer as a book names them (requirement 0118). */
export interface BookWriter {
  id: string;
  name: string;
  role: "author" | "translator";
  position: number;
  /** Signed on the single book's response only; absent draws the initial. */
  portrait?: WriterPortrait;
}

/** A writer's page: the writer and their books this viewer may read. */
export interface WriterPage {
  writer: Writer;
  books: Book[];
}

/** A writer's portrait, a signed link (0118 W3). */
export interface WriterPortrait {
  url: string;
  width: number;
  height: number;
}

/** A writer's own row, as the studio edits it. */
export interface Writer {
  id: string;
  name: string;
  dates?: string;
  bio?: string;
  /** Only on the writer's page and edit form; absent draws the initial. */
  portrait?: WriterPortrait;
}

/** One writer a book names, as the studio sends it: by id, or a new name. */
export interface WriterChoice {
  id?: string;
  name?: string;
  role: BookWriter["role"];
}

/**
 * writerOptions is the studio picker's search for a book: writers its library
 * can already see, never a stranger's private one, and SageLeaf's own writer
 * apart, to offer as a translator only.
 */
export function writerOptions(
  bookID: string,
  q: string,
): Promise<{ items: Writer[]; sageleaf: Writer }> {
  return request(`/api/v1/books/${bookID}/writer-options?q=${encodeURIComponent(q)}`);
}

/** A writer for its edit form; 403 when it is shared and a platform admin's. */
export function getEditableWriter(id: string): Promise<Writer> {
  return request<Writer>(`/api/v1/writers/${id}/edit`);
}

/** updateWriter edits a writer's name, dates and bio; "" clears dates or bio. */
export function updateWriter(
  id: string,
  patch: { name?: string; dates?: string; bio?: string },
): Promise<Writer> {
  return request<Writer>(`/api/v1/writers/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

/**
 * uploadWriterPortrait makes a picture the writer's portrait (0118 W3): free,
 * writer.write, the same 12 MB as a character's upload.
 */
export function uploadWriterPortrait(id: string, file: Blob): Promise<Writer> {
  return request<Writer>(`/api/v1/writers/${id}/portrait/upload`, { method: "POST", body: file });
}

/**
 * drawWriterPortrait draws the writer from their name, dates and bio in the
 * house style: one paid picture, a few cents, on the library's budget.
 */
export function drawWriterPortrait(id: string): Promise<Writer> {
  return request<Writer>(`/api/v1/writers/${id}/portrait/draw`, { method: "POST" });
}

/** getWriter is 404 for a writer with no book this viewer may read. */
export function getWriter(id: string): Promise<WriterPage> {
  return request<WriterPage>(`/api/v1/writers/${id}`);
}

/** bookKey is the cache key for a book read as one of its bodies. */
export function bookKey(id: string, opts: ViewOptions = {}): string[] {
  return opts.draft ? ["book", id, "draft"] : ["book", id];
}

export function listChapters(
  bookID: string,
  opts: ViewOptions = {},
): Promise<{ items: Chapter[] }> {
  return request<{ items: Chapter[] }>(`/api/v1/books/${bookID}/chapters${viewQuery(opts)}`);
}

export function getChapter(id: string, opts: ViewOptions = {}): Promise<ChapterText> {
  return request<ChapterText>(`/api/v1/chapters/${id}${viewQuery(opts)}`);
}

export function createChapter(
  bookID: string,
  body: { title: string; body: string; emphasis?: EmphasisRange[] },
): Promise<ChapterText> {
  return request<ChapterText>(`/api/v1/books/${bookID}/chapters`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * One chapter an EPUB was read into (ADR-0052). `title` is empty when the file
 * has none; `chars` and `too_long` are the server's count and limit, so the
 * client never counts.
 */
export interface ImportedChapter {
  title: string;
  body: string;
  chars: number;
  too_long: boolean;
  /** The file's italics, sent back unchanged with the chapter (ADR-0205). */
  emphasis?: EmphasisRange[];
}

/** What an EPUB holds. `language` is one the studio offers, or empty. */
export interface EpubImport {
  title: string;
  author: string;
  language: string;
  chapters: ImportedChapter[];
}

/** Reads an EPUB into chapters. The body is the file; nothing is saved. */
export function readEPUB(bookID: string, file: Blob): Promise<EpubImport> {
  return request<EpubImport>(`/api/v1/books/${bookID}/imports/epub`, {
    method: "POST",
    body: file,
  });
}

/**
 * Appends chapters to a book, all of them or none. A book with no language or
 * author takes the ones given here.
 */
export function addChapters(
  bookID: string,
  body: {
    chapters: { title: string; body: string; emphasis?: EmphasisRange[] }[];
    language: string;
    author: string;
  },
): Promise<{ items: Chapter[] }> {
  return request<{ items: Chapter[] }>(`/api/v1/books/${bookID}/chapters/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** One turn of an author's rewrite conversation (ADR-0025). */
/**
 * A picture in a chapter (ADR-0038).
 *
 * `paragraph` is which paragraph it follows, -1 for the top of the chapter.
 * `placed` is false when that paragraph is no longer in the text — an edit
 * orphaned it, and the studio says so rather than moving it onto other words.
 */
export interface ChapterImage {
  id: string;
  url: string;
  /** The stored picture, which a crop starts from (ADR-0048). */
  object_key: string;
  width: number;
  height: number;
  paragraph: number;
  placed: boolean;
  /** Byte offset just past the anchoring paragraph, for the player. */
  text_offset: number;
  caption?: string;
  alt?: string;
  prompt?: string;
  /**
   * How the picture was made. Absent on a reader's read: an edition freezes
   * what a reader is served, not the studio's record of how it was drawn
   * (ADR-0062).
   */
  source?: "upload" | "generated";
  /** The paragraph it was placed against, so an orphan can say what it followed. */
  anchor_text?: string;
  /**
   * The characters whose portraits this picture was drawn from (ADR-0040) —
   * the visible sign that they will look the same in the next picture too.
   */
  drawn_from?: string[];
  /** The same characters by id, so replacing the picture starts from them (ADR-0053). */
  character_ids?: string[];
  /** The forms its characters were drawn in, beside character_ids (ADR-0159). */
  form_ids?: string[];
  /**
   * Whether readers have this picture as it stands (ADR-0062). In the studio,
   * false is the mark that a placement, a caption or a crop is still a draft;
   * on a reader's read everything listed is published by construction.
   */
  published: boolean;
}

/** A picture that exists in the object store but is in no chapter yet. */
export interface ImageCandidate {
  object_key: string;
  url: string;
  width: number;
  height: number;
  prompt?: string;
  cost_micros: number;
  /**
   * Which model drew it (ADR-0086), as the server reports it. Carried back
   * when it is placed, so the row records the model that was paid for rather
   * than whatever the sheet happens to be set to by then.
   */
  quality?: ImageQuality;
  /** The characters it was drawn from, and their ids for placing it. */
  drawn_from: string[];
  character_ids: string[];
  /** The forms its characters were drawn in, beside character_ids (ADR-0159). */
  form_ids?: string[];
  /**
   * The earlier pictures actually sent with it (ADR-0072) — what the cap of
   * four left room for after the faces, not what was asked for. Carried
   * through to the placement so the row records what it was drawn beside.
   */
  reference_image_ids?: string[];
}

/**
 * One picture a book already has, offered as a continuity reference
 * (ADR-0072, requirement 0029).
 *
 * The description travels with the picture: the author picks a picture they
 * can see, and the words it was drawn from go along without being retyped.
 * Empty for an upload, which has none.
 */
export interface BookPicture {
  id: string;
  chapter_id: string;
  chapter_title: string;
  url: string;
  width: number;
  height: number;
  prompt: string;
  source: "upload" | "generated";
}

/** Every working picture of a book, in reading order. A studio read. */
export function getBookPictures(bookID: string): Promise<{ pictures: BookPicture[] }> {
  return request(`/api/v1/books/${bookID}/images`);
}

/** The shapes a generated picture can take. */
export const IMAGE_SHAPES = ["4:3", "3:4", "1:1", "16:9"] as const;
export type ImageShape = (typeof IMAGE_SHAPES)[number];

/**
 * Which model draws a picture (ADR-0086): the cheap one an author iterates
 * with, or the expensive one for a picture that is kept.
 *
 * A *quality*, never a model name — the server owns the map, so a client
 * cannot ask for a model with no price. And never a default: a sheet opens on
 * "standard" every time, including straight after one that drew with "pro".
 */
export type ImageQuality = "standard" | "pro";

/**
 * What a picture costs at one quality, formatted by the server.
 *
 * The server sends one per quality it offers and the client shows a chip for
 * each: tapping one must not cost a round trip, and the client never holds its
 * own idea of which qualities exist or turns micros into words.
 */
export interface DrawPrice {
  quality: ImageQuality;
  micros: number;
  display: string;
}

/**
 * How ready a book's characters are to be drawn from (ADR-0039).
 *
 * A scene is drawn from the portraits of the characters it names, so a
 * chapter cannot be illustrated until the book has been read for its cast.
 * Faces are checked per picture, against its description (ADR-0044).
 */
export interface Cast {
  /** The cast has been read, so a picture may be drawn. */
  ready: boolean;
  /** Whether the roster has been read at all. An empty roster only means "no
   * characters" once somebody has looked. */
  extracted: boolean;
  total: number;
  with_portraits: number;
  /** Chapters changed since they were read. Reported, never blocking. */
  stale: number;
  /** The characters still without a portrait, up to a handful. */
  missing: string[];
}

/** What a whole auto-illustration run costs, per number of pictures. */
export interface RunEstimate {
  spots: number;
  micros: number;
  display: string;
}

/** One picture an auto-illustration run is making (ADR-0074). */
export interface RunSpot {
  position: number;
  paragraph: number;
  subject: string;
  /** `cancelled`: its run was stopped before it was drawn or placed (ADR-0167). */
  status: "pending" | "done" | "failed" | "cancelled";
  /** A stable code the studio translates; never the provider's own words. */
  reason?: string;
  /** Who it is waiting on a face for, when the reason is needs_face. */
  who?: string[];
  image_id?: string;
}

export interface IllustrationRun {
  id: string;
  /** `cancelled` is a run somebody stopped: settled, and not a failure (ADR-0167). */
  status: "planning" | "drawing" | "done" | "failed" | "cancelled";
  reason?: string;
  /** What the author asked for, and was priced for (ADR-0085). */
  wanted: number;
  /**
   * Spots the plan produced, and the denominator for progress. Fewer than
   * `wanted` when the spacing rule dropped a paragraph; zero while planning.
   */
  planned: number;
  done: number;
  failed: number;
  spots: RunSpot[];
  started_at: string;
}

/** Illustrates a whole chapter: one plan call, then one picture at a time. */
export function startIllustrationRun(chapterID: string, spots: number): Promise<IllustrationRun> {
  return request<IllustrationRun>(`/api/v1/chapters/${chapterID}/illustrate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ spots }),
  });
}

/**
 * Stops the chapter's live run (ADR-0167) and answers with the latest run.
 * Pictures already placed stay; the one being drawn is paid for and not
 * placed. With no live run it changes nothing.
 */
export function cancelIllustrationRun(chapterID: string): Promise<{ run: IllustrationRun | null }> {
  return request(`/api/v1/chapters/${chapterID}/illustrate/cancel`, { method: "POST" });
}

/** The latest run of a chapter, or null when there has never been one. */
export function getIllustrationRun(chapterID: string): Promise<{ run: IllustrationRun | null }> {
  return request(`/api/v1/chapters/${chapterID}/illustrate`);
}

export interface ChapterImages {
  items: ChapterImage[];
  /** Studio only; readers are told nothing about the cast's readiness. */
  cast?: Cast;
  /**
   * What drawing one picture costs at each quality offered, priced server-side
   * from the rate table in force. On the button before anyone presses it.
   */
  draw_prices: DrawPrice[];
  /**
   * What a whole run costs, per number of pictures (ADR-0074). A schedule
   * rather than a unit price: the plan call is paid for once, so eight
   * pictures is not eight times one.
   */
  run_estimates?: RunEstimate[];
}

export function listChapterImages(
  chapterID: string,
  opts: ViewOptions = {},
): Promise<ChapterImages> {
  return request<ChapterImages>(`/api/v1/chapters/${chapterID}/images${viewQuery(opts)}`);
}

/**
 * A picture description and who is in it (ADR-0047). The cast is the model's
 * answer, by id, which the studio holds beside the words and sends to the draw.
 */
export interface PictureDescription {
  text: string;
  cost_micros: number;
  /** The characters the picture shows, most prominent first. */
  characters?: string[];
  /** The same characters, in the same order, for the draw. */
  character_ids?: string[];
  /** The forms its characters were drawn in, beside character_ids (ADR-0159). */
  form_ids?: string[];
  /** Those among them with no portrait yet; drawing would be refused. */
  needs_faces?: string[];
}

/**
 * One turn of the conversation about what the picture shows. Costs a fraction
 * of a cent; drawing it is the expensive step, and a separate call.
 */
export function describeImage(
  chapterID: string,
  body: {
    paragraph: number;
    current?: string;
    /** Who the current description shows, so a follow-up keeps them. */
    character_ids?: string[];
    form_ids?: string[];
    /** Earlier pictures chosen for continuity; the brief is told their words. */
    reference_image_ids?: string[];
    earlier?: string[];
    instruction: string;
  },
): Promise<PictureDescription> {
  return request(`/api/v1/chapters/${chapterID}/images/describe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Draws a picture. Spends on every call; the result is not in the chapter. */
export function generateImage(
  chapterID: string,
  body: {
    prompt: string;
    shape: ImageShape;
    /** Which model draws it (ADR-0086). Absent is standard. */
    quality?: ImageQuality;
    character_ids: string[];
    /** The forms they are drawn in: each form's own portrait goes (ADR-0159). */
    form_ids?: string[];
    /** Earlier pictures sent as references, for the place (ADR-0072). */
    reference_image_ids?: string[];
  },
): Promise<ImageCandidate> {
  return request<ImageCandidate>(`/api/v1/chapters/${chapterID}/images/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * Uploads a picture. The body is the file itself: there is one field, and
 * base64 in JSON would inflate a 12 MB photograph by a third. The server reads
 * the format from the bytes, so no Content-Type is claimed here.
 */
export function uploadImage(chapterID: string, file: Blob): Promise<ImageCandidate> {
  return request<ImageCandidate>(`/api/v1/chapters/${chapterID}/images/upload`, {
    method: "POST",
    body: file,
  });
}

/**
 * The part of a picture to keep, in fractions (0–1) of the picture as it is
 * displayed, so the client never needs its stored size or how a phone turned
 * it (ADR-0048).
 */
export interface CropRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

function cropAt(path: string, objectKey: string, region: CropRegion): Promise<ImageCandidate> {
  return request<ImageCandidate>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ object_key: objectKey, ...region }),
  });
}

/** Crops a stored picture for a chapter into a new candidate. Free. */
export function cropImage(
  chapterID: string,
  objectKey: string,
  region: CropRegion,
): Promise<ImageCandidate> {
  return cropAt(`/api/v1/chapters/${chapterID}/images/crop`, objectKey, region);
}

/** Puts a stored picture into the chapter, after a paragraph. */
export function placeImage(
  chapterID: string,
  body: {
    object_key: string;
    paragraph: number;
    caption?: string;
    alt?: string;
    prompt?: string;
    source: "upload" | "generated";
    /** Which model drew it, so the row records the one that was paid for. */
    quality?: ImageQuality;
    character_ids?: string[];
    form_ids?: string[];
    reference_image_ids?: string[];
  },
): Promise<ChapterImage> {
  return request<ChapterImage>(`/api/v1/chapters/${chapterID}/images`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function updateImage(
  imageID: string,
  body: {
    caption?: string;
    alt?: string;
    paragraph?: number;
    object_key?: string;
  } & Partial<Replacement>,
): Promise<ChapterImage> {
  return request<ChapterImage>(`/api/v1/images/${imageID}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * What a replacement brings to a placed picture (ADR-0053): sent with its
 * `object_key`, all three together. `object_key` alone is a re-crop.
 */
export interface Replacement {
  prompt: string;
  source: "upload" | "generated";
  /** Which model drew the replacement (ADR-0086). */
  quality?: ImageQuality;
  character_ids: string[];
  /** The forms they were drawn in (ADR-0159). Optional. */
  form_ids?: string[];
  /** The earlier pictures it was drawn beside (ADR-0072). Optional. */
  reference_image_ids?: string[];
}

export function deleteImage(imageID: string): Promise<void> {
  return request<void>(`/api/v1/images/${imageID}`, { method: "DELETE" });
}

export interface RewriteBody {
  /** The selected text as it stands in the editor. */
  passage: string;
  /** The latest version, or omitted on the first turn. */
  current?: string;
  /** Text either side of the selection; the server keeps what it needs. */
  before?: string;
  after?: string;
  /** Instructions already given, oldest first. */
  earlier?: string[];
  instruction: string;
}

export interface Rewrite {
  text: string;
  cost_micros: number;
}

/**
 * rewritePassage asks for a new version of a passage.
 *
 * It spends the book's library budget, so it fires only on an explicit send.
 * It saves nothing: the text reaches the chapter only when the author places it
 * in the editor and saves.
 */
export function rewritePassage(chapterID: string, body: RewriteBody): Promise<Rewrite> {
  return request<Rewrite>(`/api/v1/chapters/${chapterID}/rewrites`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** setChapterText creates a new revision; it never edits one in place. */
export function setChapterText(id: string, body: string): Promise<ChapterText> {
  return request<ChapterText>(`/api/v1/chapters/${id}/text`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
}

export function reorderChapters(
  bookID: string,
  chapterIDs: string[],
): Promise<{ items: Chapter[] }> {
  return request<{ items: Chapter[] }>(`/api/v1/books/${bookID}/chapters/order`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ chapter_ids: chapterIDs }),
  });
}

export function renameChapter(id: string, title: string): Promise<Chapter> {
  return request<Chapter>(`/api/v1/chapters/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
}

export function deleteChapter(id: string): Promise<void> {
  return request<void>(`/api/v1/chapters/${id}`, { method: "DELETE" });
}

/** Word is one word with its measured position in the chapter's timeline. */
export interface Word {
  text: string;
  start_ms: number;
  end_ms: number;
  offset: number;
  length: number;
  matched?: boolean;
  /**
   * The text after the word up to the next one — punctuation, quotes, line
   * breaks — and, on a part's first word only, the text before it. Absent from
   * a server older than them; the player then puts a space after each word.
   */
  after?: string;
  before?: string;
}

export interface Sentence {
  text: string;
  start_ms: number;
  end_ms: number;
  offset: number;
  length: number;
  words: Word[];
}

export interface Part {
  index: number;
  url: string;
  duration_ms: number;
  start_ms: number;
  /** Null or empty for a part whose alignment failed. */
  sentences: Sentence[] | null;
}

/**
 * Recording is how one render was made: what the audio in hand was narrated
 * with, not what the book is set to narrate with next.
 *
 * No cost in it. What a chapter cost is the library's business and lives on the
 * usage page; this is shown to readers.
 */
export interface Recording {
  model: string;
  voice: string;
  mode: RenderMode;
  /** The part size; absent on a render made before it was a book setting. */
  part_chars?: number;
  read_context: boolean;
  prepared: boolean;
  parts: number;
  made_at: string;
}

export interface Manifest {
  chapter_id: string;
  render_id: string;
  /**
   * The revision this audio was recorded from. The studio compares it with the
   * chapter's current revision: the transcript is the audio's text, so once the
   * text has been edited it is no longer what the chapter says.
   */
  revision_id?: string;
  status: string;
  duration_ms: number;
  expires_at: string;
  parts: Part[];
  /**
   * How this audio was made. Optional because a rolling deploy runs
   * the old binary and the new client against each other for a minute, and a
   * whole chapter must not fail to render over a badge.
   */
  recording?: Recording;
  /**
   * The italics of the revision this audio was recorded from, as byte spans
   * of it — the unit each word's `offset` is in (ADR-0205).
   */
  emphasis?: Spans;
}

/** Why a part failed (ADR-0120). */
export type PartFailure =
  | "unsaid_run"
  | "stopped_early"
  | "lead_in_miss"
  | "refused"
  | "account"
  | "provider_error"
  | "timed_out"
  | "unknown"
  | "cue_spoken"
  | "voice_gone"
  | "extra_speech";

export interface RenderPart {
  index: number;
  status: string;
  align_status: string;
  duration_ms: number;
  reused: boolean;
  error?: string;
  /** A failed part only: shown as its passage, never its number (ADR-0120). */
  id?: string;
  text?: string;
  failure?: PartFailure;
  /** The words not said, as [start, end) runes of `text`, when known. */
  unsaid_start?: number;
  unsaid_end?: number;
  /** Whether asking again can give a different answer; the server decides. */
  retryable?: boolean;
}

export interface Render {
  id: string;
  chapter_id: string;
  mode: RenderMode;
  /** The voice and speech model this render was recorded with — facts about
   *  the audio, not settings. Both were hashed into the render key, so they
   *  cannot drift from what people are actually hearing. */
  voice: string;
  model: string;
  status: string;
  chunk_total: number;
  chunk_done: number;
  cost_micros: number;
  cost_usd: string;
  error?: string;
  /** Narrated from the chapter's script with a voice for every character. */
  performed?: boolean;
  parts?: RenderPart[];
}

export interface Estimate {
  chunks: number;
  reusable: number;
  /**
   * Parts re-levelled from audio already bought, at no cost (ADR-0178): not
   * reusable, because what is heard changes, and not re-recorded either.
   */
  refinish?: number;
  duration_ms: number;
  /**
   * True when the chapter's desired render key differs from the one listeners
   * are hearing — an edit or a saved correction that has not been narrated yet.
   * One hash comparison on the server, not a scan.
   */
  needs_render: boolean;
  /**
   * The voice and model this estimate was priced and keyed over. Compared
   * against the render being listened to: a difference in either means every
   * part is re-recorded, which `reusable` does not say on its own.
   */
  voice: string;
  model: string;
  /**
   * Occurrence fixes whose anchored span no longer matches the text. They are
   * already excluded from the render; these ids are what lets the UI say so.
   */
  detached_fixes: string[];
  /**
   * Narrated from the chapter's performance script with a voice for every
   * character (ADR-0157), and the characters whose lines have a voice.
   */
  performed?: boolean;
  /** A character's voice (character_id), or an extra's (extra, and the speakers it reads). */
  voices?: {
    character_id?: string;
    extra?: string;
    speakers?: string[];
    name: string;
    lines: number;
  }[];
  estimate: {
    /** What narrating now would cost: only the parts that would be recorded. */
    remaining_micros: number;
    remaining_display: string;
    micros: number;
    low_micros: number;
    high_micros: number;
    display: string;
    display_range: string;
  };
}

/** Scope decides which chapters a correction reaches (ADR-0009). */
export type FixScope = "occurrence" | "chapter" | "book" | "org";

/**
 * A `replace` rewrites the word; an `instruct` leaves the text alone and adds a
 * line to a preamble the model obeys and never speaks. Finglish belongs in a
 * hint and never in a replacement — inline Latin makes the model refuse to
 * generate at all.
 */
export type FixKind = "replace" | "instruct";

export interface Fix {
  id: string;
  scope: FixScope;
  kind: FixKind;
  from_text: string;
  to_text?: string;
  hint?: string;
  book_id?: string;
  chapter_id?: string;
  anchor?: { revision_id: string; offset: number; length: number };
  detached: boolean;
  /**
   * A narrower correction for the same word is in force, so this one reaches
   * the chapter and changes nothing about it (ADR-0036). Computed per chapter:
   * the same library correction is in force in one chapter and overridden in
   * the next.
   */
  superseded: boolean;
  /**
   * Set only when the corrections were asked for about a word: "word" for a
   * correction for it as written, "variant" for one saved against another
   * spelling of the same word — which reaches this chapter and does nothing to
   * this word, because a fix finds its word byte for byte (ADR-0036).
   */
  match?: "word" | "variant";
  created_at: string;
}

/** Candidate is a respelling to listen to, with the reason it is offered. */
export interface Candidate {
  text: string;
  reason: string;
}

export interface FixPreview {
  url: string;
  duration_ms: number;
  cost_micros: number;
  text: string;
  instructions?: string;
  /** What the hinted word is sent as on ElevenLabs, without slashes (ADR-0035). */
  ipa?: string;
  remaining_this_hour: number;
}

/**
 * listFixes returns the corrections that reach a chapter.
 *
 * With `word`, it returns only the ones about that word — including any saved
 * against another spelling of it — each carrying `match`. The folding is the
 * server's, so "the same word" means one thing here and in the render.
 */
export function listFixes(chapterID: string, word?: string): Promise<{ items: Fix[] }> {
  const query = word ? `?word=${encodeURIComponent(word)}` : "";
  return request<{ items: Fix[] }>(`/api/v1/chapters/${chapterID}/fixes${query}`);
}

export function createFix(
  chapterID: string,
  body: {
    scope: FixScope;
    kind: FixKind;
    from_text: string;
    to_text?: string;
    hint?: string;
    anchor?: { revision_id: string; offset: number; length: number };
  },
): Promise<Fix> {
  return request<Fix>(`/api/v1/chapters/${chapterID}/fixes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteFix(fixID: string): Promise<void> {
  return request<void>(`/api/v1/fixes/${fixID}`, { method: "DELETE" });
}

export function fixCandidates(word: string): Promise<{ items: Candidate[] }> {
  return request<{ items: Candidate[] }>(
    `/api/v1/fixes/candidates?word=${encodeURIComponent(word)}`,
  );
}

/**
 * previewFix renders one corrected sentence so it can be listened to.
 *
 * It spends money — a per-tap charge, rate-limited server-side at 60/hour — so
 * callers must not fire it on hover, on mount, on a debounce, or on any path
 * the user did not explicitly ask for. One tap, one charge.
 *
 * The sentence is sent as text and the server requires it to appear in the
 * chapter as it currently stands, so this can only ever narrate words already
 * there. A 409 means the chapter moved on and the page should be reloaded.
 */
export function previewFix(body: {
  chapter_id: string;
  sentence: string;
  kind: FixKind;
  from_text: string;
  to_text?: string;
  hint?: string;
}): Promise<FixPreview> {
  return request<FixPreview>(`/api/v1/fixes/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export interface Progress {
  chapter_id: string;
  book_id?: string;
  position_ms: number;
  /** The furthest point reached; it never goes back. */
  fraction: number;
  /** Where the reader last was (ADR-0058); absent from an older server. */
  place?: number;
  completed: boolean;
  updated_at?: string;
}

export function getManifest(chapterID: string, opts: ViewOptions = {}): Promise<Manifest> {
  return request<Manifest>(`/api/v1/chapters/${chapterID}/parts${viewQuery(opts)}`);
}

/** `fresh` prices a fresh take: every part, none reused (ADR-0162). */
export function getRenderEstimate(chapterID: string, fresh = false): Promise<Estimate> {
  return request<Estimate>(
    `/api/v1/chapters/${chapterID}/renders/estimate${fresh ? "?fresh=true" : ""}`,
  );
}

/**
 * Mode is the price point.
 *
 * `fast` returns audio while you wait. `economy` is the same audio at half
 * price, delivered when the provider gets to it — measured at 4 minutes for a
 * probe and 72 minutes for a twelve-part chapter, against a 24-hour SLA. It is
 * part of the render key, so a chapter is one or the other and never both.
 */
export type RenderMode = "fast" | "economy";

export function startRender(
  chapterID: string,
  mode: RenderMode = "fast",
  voice?: string,
  fresh = false,
): Promise<Render> {
  return request<Render>(`/api/v1/chapters/${chapterID}/renders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // An absent voice means the book's, then the installation's. Sending "" is
    // the same thing; sending a name the server does not know is a 400 before
    // anything is charged.
    // `fresh` is Record again: every part recorded anew (ADR-0162).
    body: JSON.stringify({ mode, ...(voice ? { voice } : {}), ...(fresh ? { fresh } : {}) }),
  });
}

/** One piece of a chapter's narrated text and its preparation (ADR-0033). */
export interface PreparedPiece {
  /** Absent for a piece never prepared. */
  id?: string;
  index: number;
  source: string;
  /** The source with the marks added, once complete. */
  prepared?: string;
  /** pending, running, complete or failed; "" for a piece never prepared. */
  status: string;
  error?: string;
  /**
   * A stretch the model rewrote instead of marking. The piece is prepared —
   * that stretch is the author's text, without marks (ADR-0033).
   */
  note?: string;
  attempts: number;
  marks: number;
}

export interface Preparation {
  pieces: PreparedPiece[];
  total: number;
  complete: number;
  pending: number;
  failed: number;
  marks: number;
  /**
   * Whether this book can be prepared at all. Preparing adds kasre-ye ezafe
   * (ADR-0033), which is Persian; on a Korean book the panel is not shown
   * rather than offering a paid call that can only damage the text.
   */
  available: boolean;
}

export function getPreparation(chapterID: string): Promise<Preparation> {
  return request<Preparation>(`/api/v1/chapters/${chapterID}/preparation`);
}

/** startPreparation queues every piece not yet prepared, and failed ones again. */
export function startPreparation(chapterID: string): Promise<Preparation> {
  return request<Preparation>(`/api/v1/chapters/${chapterID}/preparation`, {
    method: "POST",
  });
}

export function clearPreparation(chapterID: string): Promise<void> {
  return request<void>(`/api/v1/chapters/${chapterID}/preparation`, {
    method: "DELETE",
  });
}

/** updatePreparedPiece replaces a piece's marks; the server refuses any other change. */
export function updatePreparedPiece(
  chapterID: string,
  pieceID: string,
  prepared: string,
): Promise<Preparation> {
  return request<Preparation>(`/api/v1/chapters/${chapterID}/preparation/${pieceID}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prepared }),
  });
}

/** A mark in a performance script, by byte offset in its paragraph (ADR-0157). */
export interface ScriptMark {
  at: number;
  kind: "cue" | "pause" | "long_pause" | "stop" | "speaker";
  /** A cue's words, without brackets, or a speaker's name. */
  text?: string;
  /** A speaker's extra voice. */
  extra?: ExtraKind;
}

/**
 * The extra voices a book may cast for speakers its roster does not name
 * (ADR-0157 §9), in the order the studio lists them.
 */
export const EXTRA_KINDS = [
  "older_man",
  "younger_man",
  "older_woman",
  "younger_woman",
  "child",
] as const;
export type ExtraKind = (typeof EXTRA_KINDS)[number];

/** A quoted line of a paragraph and who says it — characters', read-only here. */
export interface ScriptLine {
  ordinal: number;
  /** Byte offsets in the paragraph's source, quote marks included. */
  start: number;
  end: number;
  text: string;
  /** The attribution a speaker change corrects (correctLine); absent until attributed. */
  quote_id?: string;
  label?: DialogueLabel;
  character_id?: string;
  speaker?: string;
  cue?: string;
  stop?: boolean;
  /** Who says a someone-else line, and the extra voice that reads it: the script's speaker mark. */
  named?: string;
  extra?: ExtraKind;
}

export interface ScriptParagraph {
  index: number;
  id?: string;
  source: string;
  /** The paragraph with its marks, once it has a script. */
  script?: string;
  /** pending, running, complete or failed; "" for a paragraph never prepared. */
  status: string;
  error?: string;
  note?: string;
  attempts: number;
  edited?: boolean;
  lines: ScriptLine[];
  marks: ScriptMark[];
  cues: number;
  pauses: number;
  speakers: number;
}

/** A chapter's performance script: the chapter prepared for a voice per character. */
export interface PerformanceScript {
  paragraphs: ScriptParagraph[];
  total: number;
  complete: number;
  pending: number;
  failed: number;
  cues: number;
  pauses: number;
  speakers: number;
  /** Someone-else lines with no speaker named; a press asks them again. */
  unnamed: number;
  /** The whole script, paragraphs a blank line apart. */
  text: string;
  available: boolean;
  unavailable?: string;
}

export function getScript(chapterID: string): Promise<PerformanceScript> {
  return request<PerformanceScript>(`/api/v1/chapters/${chapterID}/performance-script`);
}

/** prepareScript writes the script of every paragraph without one, in one model call. */
export function prepareScript(chapterID: string): Promise<PerformanceScript> {
  return request<PerformanceScript>(`/api/v1/chapters/${chapterID}/performance-script`, {
    method: "POST",
  });
}

/** updateScript replaces one paragraph's script; only cues, pauses and a quote's comma may change. */
export function updateScript(
  chapterID: string,
  paragraph: number,
  text: string,
): Promise<PerformanceScript> {
  return request<PerformanceScript>(`/api/v1/chapters/${chapterID}/performance-script`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paragraph, text }),
  });
}

export function clearScript(chapterID: string): Promise<void> {
  return request<void>(`/api/v1/chapters/${chapterID}/performance-script`, {
    method: "DELETE",
  });
}

/** Voice is one narration voice, with a sample of it reading one sentence. */
export interface Voice {
  name: string;
  descriptor: string;
  /**
   * What to show when `name` is a vendor's identifier rather than a word:
   * ElevenLabs knows George as `JBFqnCBsd6RMkjVDRZzb` (ADR-0109).
   */
  label?: string;
  /**
   * The recorded clip, **absent when there is nothing to audition** — a model
   * with one voice is a choice of model, not of voice, so it has no clip and
   * gets no play control (ADR-0109 §6).
   *
   * Never rebuilt from the name by a caller. Samples are per model now
   * (`/voices/qwen3-tts-1.7b/ryan.mp3`), and two vendors may each have a
   * `ryan`; this field exists precisely so nobody has to know that.
   */
  sample_url?: string;
}

export interface VoiceCatalogue {
  /**
   * Every model's voices flattened.
   *
   * @deprecated Read `models[].voices`. This is the union across models and
   * says nothing about which model can speak which.
   */
  voices: Voice[];
  /** What every sample says — the same words in each, so they compare. */
  sentence: string;
  /** What a book with no voice of its own is narrated in. */
  default: string;
  /** The speech models a book can choose (ADR-0028). */
  models?: NarrationModel[];
  /** What a book that has not chosen a model is narrated in. */
  default_model?: string;
}

export interface NarrationModel {
  name: string;
  label: string;
  /**
   * The voices **this model** is narrated in, in picker order, and what a book
   * that has not chosen gets (ADR-0109). A model with one voice offers no
   * choice, and the picker says which voice it is rather than listing it.
   */
  voices?: Voice[];
  default_voice?: string;
  /** Whether economy mode is available on this model. */
  batch?: boolean;
  /** The largest part size this model is split at, when it has one (ADR-0031). */
  max_part_chars?: number;
  /**
   * The model is sent only the text: a hint does nothing unless it reads IPA,
   * and a book's style direction is not offered at all (ADR-0109, ADR-0110).
   */
  no_direction?: boolean;
  /** Hints on this model are sent as IPA, not as instructions (ADR-0035). */
  reads_ipa?: boolean;
  /**
   * The only book languages this model is offered on, when it has any
   * (ADR-0113): Kokoro is English only. Absent means every language.
   */
  languages?: string[];
  /** Read-and-cut does nothing on this model, so it is not offered (ADR-0113). */
  takes_no_lead_in?: boolean;
  /** Voices can be designed for each character on this model, and it narrates from a performance script (ADR-0157). */
  performs?: boolean;
}

/**
 * listVoices is a constant: the same models, voices and files for everyone, and
 * no session needed. Cached hard by the caller for that reason.
 */
export function listVoices(): Promise<VoiceCatalogue> {
  return request<VoiceCatalogue>(`/api/v1/voices`);
}

/** Language is one language a book can be written in. */
export interface Language {
  code: string;
  /** The name a brief written in English uses. */
  english: string;
  /** The language named in itself, which is what somebody choosing it reads. */
  endonym: string;
  /** Its name in Persian, for searching from the Persian interface. */
  persian?: string;
  /** Offered before any search (ADR-0056). */
  common: boolean;
}

/**
 * listLanguages is a constant too, and served rather than held here because the
 * same list decides what the language models are told to answer in.
 */
export function listLanguages(): Promise<{ items: Language[] }> {
  return request<{ items: Language[] }>(`/api/v1/languages`);
}

/**
 * latestRender is what is happening to this chapter right now, if anything.
 *
 * The render id used to live only in the component state of the tab that
 * pressed Narrate, so a refresh lost track of a job that was still running.
 * Rejects with a 404 when the chapter has never been rendered, which is a state
 * rather than a failure.
 */
export function latestRender(chapterID: string): Promise<Render> {
  return request<Render>(`/api/v1/chapters/${chapterID}/renders/latest`);
}

export function getRender(renderID: string): Promise<Render> {
  return request<Render>(`/api/v1/renders/${renderID}`);
}

/**
 * cancelRender stops a running render. Parts already narrated are kept, and
 * narrating again picks up from where it stopped.
 */
export function cancelRender(renderID: string): Promise<Render> {
  return request<Render>(`/api/v1/renders/${renderID}/cancel`, {
    method: "POST",
  });
}

export interface ChapterExport {
  status: "ready" | "building";
  url?: string;
}

/**
 * exportChapter asks for a single-file download of the whole chapter.
 *
 * Built server-side with a re-encode, never by stitching the parts in the
 * browser: concatenated MP3 frames leave encoder delay and padding at every
 * seam and leave the header claiming the first part's duration, so seeking in
 * the saved file is wrong everywhere.
 *
 * The first call returns `building` and the file is made in the background;
 * call again to get the link. It is content-addressed, so this is once per
 * distinct audio and not once per request.
 */
export function exportChapter(chapterID: string): Promise<ChapterExport> {
  return request<ChapterExport>(`/api/v1/chapters/${chapterID}/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

/** Resume is a chapter you are part-way through, for the Continue shelf. */
export interface Resume extends Progress {
  book_title: string;
  book_slug: string;
  chapter_title: string;
}

/**
 * continueListening is where the reader left off, newest first.
 *
 * Signed-in only — there is nowhere to store a position without an account —
 * so callers must not fetch it for an anonymous visitor.
 */
export function listLibrary(cursor?: string): Promise<BookList> {
  const query = cursor ? `cursor=${encodeURIComponent(cursor)}` : "";
  return request<BookList>(`/api/v1/me/library${query ? `?${query}` : ""}`);
}

/** listSharedWithMe is the books shared with the reader's groups (ADR-0153). */
export function listSharedWithMe(): Promise<BookList> {
  return request<BookList>(`/api/v1/me/shared`);
}

/**
 * saveToLibrary keeps a book, and removeFromLibrary stops keeping it.
 *
 * PUT rather than POST: saving a book already saved is the same shelf, so a
 * double tap on a phone is a no-op and not an error. Removing leaves the
 * reader's position in the book alone — save it again and they are where they
 * were.
 */
export function saveToLibrary(bookID: string): Promise<void> {
  return request<void>(`/api/v1/me/library/${bookID}`, { method: "PUT" });
}

export function removeFromLibrary(bookID: string): Promise<void> {
  return request<void>(`/api/v1/me/library/${bookID}`, { method: "DELETE" });
}

/**
 * hideFromContinue takes a book out of Pick up where you left off. The place
 * stays, and the next progress save in the book brings it back. Idempotent.
 */
export function hideFromContinue(bookID: string): Promise<void> {
  return request<void>(`/api/v1/books/${bookID}/progress/continue`, { method: "DELETE" });
}

export function continueListening(): Promise<{ items: Resume[] }> {
  return request<{ items: Resume[] }>(`/api/v1/me/continue`);
}

/** Citation points at the passage an answer came from. */
export interface Citation {
  chapter_id: string;
  chapter_title: string;
  text: string;
  offset: number;
  length: number;
}

export interface Answer {
  answer: string;
  citations: Citation[];
  cost_micros: number;
  remaining_this_hour: number;
  /**
   * Which kind of answer this is (ADR-0014): searched across the book up to
   * the reader's line (ADR-0045), or reasoned over the page the reader is on. The UI must render the two
   * differently — an ungrounded answer that looks grounded is the failure
   * citations exist to prevent.
   */
  grounded: boolean;
  /**
   * The mode this answer was written in (ADR-0071). Labelled per answer rather
   * than per conversation, because a reader may switch mid-thread and the two
   * kinds must never render alike.
   */
  mode?: AskMode;
  /**
   * A stable code for an answer no model wrote, absent for every answer one
   * did. "nothing_read_yet" is the only one: the reader has read nothing to
   * answer from, nothing was spent saying so, and the client writes the words
   * — in the reader's own interface language.
   */
  reason?: string;
}

/**
 * AskMode is what the reader may be told beyond the book (ADR-0071).
 *
 * Spoiler-free is the default and is what every answer was before: the book
 * alone, as far as they have read. Open lets the model answer from what it
 * knows as well — which is not spoiler-free and is not offered as though it
 * were.
 */
export type AskMode = "spoiler_free" | "open";

/** NOTHING_READ_YET is the reason code for a question asked before any reading. */
export const NOTHING_READ_YET = "nothing_read_yet";

/**
 * SIGN_IN_TO_CHAT is the server's refusal to a visitor (ADR-0070).
 *
 * A 401 with its own code rather than the generic one, so the chat can stay on
 * screen and answer in place. Rendered as a turn, with the wording here in the
 * reader's own language: no model is called to say it.
 */
export const SIGN_IN_TO_CHAT = "sign_in_to_chat";

/**
 * QUESTIONS_PAUSED is the site-wide question ceiling (ADR-0191): a 429 with
 * `resumes_at`. Rendered as a turn in the reader's interface language, like
 * SIGN_IN_TO_CHAT; reading and listening are unaffected.
 */
export const QUESTIONS_PAUSED = "questions_paused";

/**
 * PLUS_REQUIRED is a signed-in reader without Plus reaching a Plus book's
 * words, sound or questions (ADR-0193): a 403. Wherever it arrives, the page
 * offers Get Plus instead of an error.
 */
export const PLUS_REQUIRED = "plus_required";

/** isPlusRequired reports whether an error is the Plus refusal. */
export function isPlusRequired(err: unknown): boolean {
  return err instanceof ApiError && err.problem.code === PLUS_REQUIRED;
}

/**
 * PlusQuestion is the Get Plus question as the server serves it: keys only.
 * The wording, and the price inside it, are this client's strings — no
 * response carries a price (ADR-0193 §5).
 */
export interface PlusQuestion {
  plus: boolean;
  question: string;
  choices: string[];
}

/**
 * getPlusQuestion is the Get Plus tap: the server counts this fetch as the
 * tap (ADR-0190 §5), so call it only from the question screen, never to
 * prefetch.
 */
export function getPlusQuestion(bookID?: string): Promise<PlusQuestion> {
  const query = bookID ? `?book=${encodeURIComponent(bookID)}` : "";
  return request<PlusQuestion>(`/api/v1/me/plus${query}`);
}

/** answerPlus answers the question and gives the account Plus. */
export function answerPlus(body: {
  question: string;
  answer: string;
  note?: string;
}): Promise<PlusQuestion> {
  return request<PlusQuestion>("/api/v1/me/plus", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * askAboutChapter answers a question from the book's own text.
 *
 * It spends money — an LLM call, charged to the *reader's* own library, not the
 * author's (ADR-0013) — so it fires only on an explicit ask, never on a
 * keystroke or a debounce. Signing in is required: there is nowhere to bill a
 * visitor, so an anonymous caller gets a 401.
 *
 * `history` is the conversation so far, oldest first. The server holds no
 * conversations (ADR-0022): each follow-up carries the earlier turns and is
 * still one question and one charge.
 */
/** The questions a reader asks with one tap instead of typing (ADR-0141). */
export type AskAction = "meaning" | "explain" | "simpler";

export function askAboutChapter(
  chapterID: string,
  body: {
    question: string;
    sentence?: string;
    history?: { question: string; answer: string }[];
    /** What was said about other lines of this book (ADR-0156). */
    recent?: { chapter_id: string; line: string; question: string; answer: string }[];
    mode?: AskMode;
    /** A one-tap question (ADR-0141); `question` is still its label. */
    action?: AskAction;
    /** The word pressed, for "meaning". */
    phrase?: string;
  },
): Promise<Answer> {
  return request<Answer>(`/api/v1/chapters/${chapterID}/questions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Where a problem report points (ADR-0189). */
export type ReportPlace =
  | { place: "sentence"; chapter_id: string; sentence: string }
  | { place: "picture"; chapter_id: string; picture_id: string }
  | { place: "character"; character_id: string; chapter_id?: string };

/** ProblemReport is a reader's report as the server stored it. */
export interface ProblemReport {
  id: string;
  place: ReportPlace["place"];
  status: "open" | "fixed" | "dismissed";
  note: string;
  created_at: string;
}

/**
 * fileReport sends a reader's problem report on a sentence, a picture or a
 * portrait (requirement 0122). The note is optional. The server labels a web
 * report with its own version: this bundle is the one it serves.
 */
export function fileReport(
  bookID: string,
  body: ReportPlace & { note?: string; edition?: number },
): Promise<ProblemReport> {
  return request<ProblemReport>(`/api/v1/books/${bookID}/reports`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, client: "web" }),
  });
}

export function getProgress(chapterID: string): Promise<Progress> {
  return request<Progress>(`/api/v1/chapters/${chapterID}/progress`);
}

/**
 * getBookProgress is the reader's progress in each chapter of one book
 * (ADR-0187): what the chapter list marks read or started, each on its own.
 */
export function getBookProgress(bookID: string): Promise<{ items: Progress[] }> {
  return request<{ items: Progress[] }>(`/api/v1/books/${bookID}/progress`);
}

/**
 * ProgressSave is one write of a place. `read_to_end` says the end of the
 * chapter's text has been in view (ADR-0187); it is a claim, never returned.
 */
export type ProgressSave = Omit<Progress, "chapter_id"> & { read_to_end?: boolean };

export function saveProgress(chapterID: string, body: ProgressSave): Promise<Progress> {
  return request<Progress>(`/api/v1/chapters/${chapterID}/progress`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * SearchStatus is whether a book is grounded — indexed, so that questions are
 * answered from the book up to the reader's line, with citations — and what refreshing it costs.
 */
export interface SearchStatus {
  grounded: boolean;
  chapters: number;
  indexed: number;
  stale: number;
  /** Grounded *and* something actually indexed. A book switched on a moment
   *  ago is grounded and not yet searchable. */
  searchable: boolean;
  estimate_micros: number;
  estimate_display: string;
}

export function getSearchStatus(bookID: string): Promise<SearchStatus> {
  return request<SearchStatus>(`/api/v1/books/${bookID}/search`);
}

/**
 * enableSearch turns grounding on and indexes whatever is out of date.
 *
 * **Spends** — the book's text is embedded — so it fires on an explicit tap
 * only. It is also the refresh: calling it on an already-grounded book indexes
 * the chapters that changed and nothing else.
 */
export function enableSearch(bookID: string): Promise<SearchStatus> {
  return request<SearchStatus>(`/api/v1/books/${bookID}/search`, {
    method: "POST",
  });
}

/** disableSearch turns grounding off and deletes the book's vectors. */
export function disableSearch(bookID: string): Promise<SearchStatus> {
  return request<SearchStatus>(`/api/v1/books/${bookID}/search`, {
    method: "DELETE",
  });
}

/** A character of a book, as the studio lists it (ADR-0034). */
export interface CharacterAppearance {
  chapter_id: string;
  title: string;
  position: number;
}

/** A character's reference picture (ADR-0039). */
export interface Portrait {
  url: string;
  object_key: string;
  width: number;
  height: number;
  prompt?: string;
  source: "upload" | "generated";
}

export interface Character {
  id: string;
  name: string;
  aliases: string[];
  role: string;
  /** Their whole arc. Readers only see it behind the spoiler switch. */
  description: string;
  /** Who they are when the book first presents them: what readers see. */
  introduction: string;
  appearances: CharacterAppearance[];
  /** extracted by the model, or added by the author. */
  source: "extracted" | "author";
  /** The author changed it, so a run keeps its words. */
  edited: boolean;
  /** The reference picture every scene they are in is drawn from (ADR-0039). */
  portrait?: Portrait;
  /** A batch drawing of their face under way, or failed (ADR-0049). */
  portrait_batch?: PortraitBatch;
  /** The portrait brought to life (ADR-0127); absent until one is made. */
  live_portrait?: LivePortrait;
  /** Making their live portrait under way, or failed, like portrait_batch. */
  live_portrait_batch?: PortraitBatch;
  /** The book's narrator: readers see their head wherever no quote speaks (ADR-0137). */
  narrates: boolean;
  /** The narrator of a one-speaker book: every quote is theirs (ADR-0180). */
  speaks_alone: boolean;
  /** Their other shapes — a disguise, a younger self — each with its own face (ADR-0159). */
  forms: CharacterForm[];
}

/**
 * A form is another shape of one character (ADR-0159): Minerva as Mentes. It
 * has its own names and its own portrait, so a picture of it is drawn from
 * that face, and (on a performed book) its own voice.
 */
export interface CharacterForm {
  id: string;
  name: string;
  /** The names the text uses for this form; never the character's aliases. */
  aliases: string[];
  appearance: string;
  portrait?: Portrait;
}

export interface FormEdit {
  name: string;
  aliases: string[];
  appearance: string;
}

function formsPath(bookID: string, characterID: string, formID?: string): string {
  const base = `/api/v1/books/${bookID}/characters/${characterID}`;
  return formID ? `${base}/forms/${formID}` : base;
}

export function addForm(bookID: string, characterID: string, edit: FormEdit): Promise<Characters> {
  return request<Characters>(`${formsPath(bookID, characterID)}/forms`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(edit),
  });
}

export function updateForm(
  bookID: string,
  characterID: string,
  formID: string,
  edit: FormEdit,
): Promise<Characters> {
  return request<Characters>(formsPath(bookID, characterID, formID), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(edit),
  });
}

export function deleteForm(
  bookID: string,
  characterID: string,
  formID: string,
): Promise<Characters> {
  return request<Characters>(formsPath(bookID, characterID, formID), { method: "DELETE" });
}

/**
 * A live portrait's four pictures, signed for one response, and the content
 * key a client caches them by (ADR-0127). The studio draws them through its
 * own origin instead (livePictureURL): WebGL needs pixels it may read.
 */
export interface LivePortrait {
  key: string;
  image_url: string;
  blink_url: string | null;
  plate_url: string;
  depth_url: string;
  flat: boolean;
}

/** Why a batch portrait or a live portrait failed: codes the studio translates. */
export type PortraitBatchReason =
  "refused" | "budget" | "failed" | "timed_out" | "expired" | "portrait_changed";

export interface PortraitBatch {
  status: "pending" | "failed";
  reason?: PortraitBatchReason;
}

/** CharacterExtraction is one run over the book, with what it cost. */
export interface CharacterExtraction {
  id: string;
  status: "running" | "complete" | "partial" | "failed";
  model: string;
  chapter_total: number;
  chapter_done: number;
  chapter_failed: number;
  characters: number;
  input_tokens: number;
  output_tokens: number;
  cost_micros: number;
  cost_display: string;
  error?: string;
  started_at?: string;
  finished_at?: string;
}

export interface Characters {
  characters: Character[];
  extraction: CharacterExtraction | null;
  chapter_total: number;
  /** Chapters not read at their current text. */
  stale: number;
  estimate_micros: number;
  estimate_display: string;
  /** What drawing one portrait costs at each quality, priced by the server
   *  (ADR-0039, ADR-0086). */
  draw_prices: DrawPrice[];
  /** One portrait drawn in a batch — words and picture — in micros (ADR-0049). */
  batch_portrait_micros: number;
  /** One live portrait, its GPU time only, in micros (ADR-0127). */
  live_portrait_micros: number;
  /** Whether this deployment can make live portraits at all. */
  live_portraits_available: boolean;
  /** Who says each quoted line (ADR-0123). */
  dialogue: Dialogue;
}

/** DialogueRun is one pass over the book's quoted lines (ADR-0123). */
export interface DialogueRun {
  id: string;
  trigger: "extraction" | "translation" | "update" | "reask" | "again" | "speakers";
  /** The label model the run used, fixed when it started (ADR-0181). */
  model: string;
  status: "running" | "complete" | "partial" | "failed" | "refused";
  chapter_total: number;
  chapter_done: number;
  chapter_failed: number;
  quotes: number;
  cost_micros: number;
  cost_display: string;
  error?: string;
  started_at: string;
  finished_at?: string;
  /** Characters a speakers run added: all while it runs, the kept ones after (ADR-0169). */
  added: number;
}

/** LabelModel is one model a book may find who says each line with (ADR-0181). */
export interface LabelModel {
  id: string;
  /** What the chip says. */
  name: string;
  /** False when this server cannot call it; reason says why. */
  available: boolean;
  reason?: string;
}

/** Dialogue is the book's side of who says each line. */
export interface Dialogue {
  /** The book's label model, resolved: its choice or the house default. */
  model: string;
  /** Whether the book chose its model, rather than taking the default. */
  chosen: boolean;
  /** The chips, in order; the client copies no list. */
  models: LabelModel[];
  /** False when the book's model cannot be called here: offer nothing that would refuse. */
  available: boolean;
  /** Why not, when not available. */
  reason?: string;
  run: DialogueRun | null;
  /** Chapters labelled at their current text. */
  attributed: number;
  /** Chapters edited since, waiting for the update press. */
  changed: number;
  estimate_micros: number;
  estimate_display: string;
  /** The most asking the whole book again costs (ADR-0161). */
  again_micros: number;
  again_display: string;
  /** People the chapter notes name and the cast lacks, and the most finding their lines costs (ADR-0169). */
  speakers: number;
  speakers_micros: number;
  speakers_display: string;
  /** Counts by label: character, someone_else, not_speech, unknown. */
  labels: Record<string, number>;
  /** Each character's lines, by id. */
  lines: Record<string, number>;
  /**
   * A one-speaker book's speaker (ADR-0180), null otherwise: every quote is
   * theirs, so nothing is asked, counted or estimated, and no press is offered.
   */
  one_speaker: DialogueSpeaker | null;
}

export type DialogueLabel = "character" | "someone_else" | "not_speech" | "unknown";

/** DialogueLine is one quote placed in the text on screen, and who says it (ADR-0125). */
export interface DialogueLine {
  /** The labelled row, which a correction names. */
  id: string;
  /** The quote as written in the displayed text, marks included. */
  text: string;
  label: DialogueLabel;
  character_id: string | null;
  source: "model" | "author";
  /** The quote's paragraph in the displayed text. */
  paragraph: number;
  /** Bytes into the displayed revision's body: the unit manifest words use. */
  start_byte: number;
  end_byte: number;
}

/** DialogueSpeaker is a character a line names: its full name and face. */
export interface DialogueSpeaker {
  id: string;
  name: string;
  portrait_url: string | null;
}

export interface ChapterDialogue {
  chapter_id: string;
  /** The revision the lines are placed in: the one asked for. */
  revision_id: string;
  /** Where the labels were attributed. */
  labels_revision_id: string | null;
  /** The two differ: a quote whose paragraph changed since has no line. */
  stale: boolean;
  /** The current text is queued. */
  pending: boolean;
  lines: DialogueLine[];
  speakers: DialogueSpeaker[];
}

/**
 * getChapterDialogue is a chapter's labels placed in the revision on screen —
 * the manifest's for a narrated chapter, the current one otherwise (ADR-0125).
 */
export function getChapterDialogue(
  chapterID: string,
  revisionID?: string,
): Promise<ChapterDialogue> {
  const query = revisionID ? `?revision=${revisionID}` : "";
  return request<ChapterDialogue>(`/api/v1/chapters/${chapterID}/dialogue${query}`);
}

/**
 * ReaderSpeakers is a published chapter's speakers as a reader is served them
 * (ADR-0137, ADR-0186): the named lines, the characters and forms they name,
 * and a play's units with where each head ends. Byte offsets into the
 * published revision's body, the unit manifest words use. The app reads the
 * same response (mobile/lib/api/models.dart ChapterSpeakers).
 */
export interface ReaderSpeakers {
  lines: {
    character_id: string;
    start_byte: number;
    end_byte: number;
    paragraph: number;
    form_id?: string | null;
    /** "span" for an authored span (ADR-0206), drawn as any line is. */
    origin?: "label" | "span";
  }[];
  speakers: { id: string; name: string; portrait?: { url: string } | null }[];
  forms?: { id: string; character_id: string; name: string; portrait?: { url: string } | null }[];
  kind?: "play" | "scene" | "prose";
  units?: { start_byte: number; end_byte: number; head_end_byte: number | null }[];
}

/** getReaderSpeakers is who says each line of a published chapter. */
export function getReaderSpeakers(chapterID: string): Promise<ReaderSpeakers> {
  return request<ReaderSpeakers>(`/api/v1/chapters/${chapterID}/speakers`);
}

/** correctLine sets one line's speaker; the label becomes the author's. */
export function correctLine(
  lineID: string,
  label: Exclude<DialogueLabel, "unknown">,
  characterID?: string,
): Promise<DialogueLine> {
  return request<DialogueLine>(`/api/v1/dialogue/${lineID}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ label, character_id: characterID ?? null }),
  });
}

/** updateDialogue is the "N chapters changed" press — on a never-labelled book the first labelling (ADR-0182): only what changed is asked. */
export function updateDialogue(bookID: string): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/dialogue`, { method: "POST" });
}

/**
 * askDialogueAgain asks every chapter again, whole, keeping only the author's
 * labels (ADR-0161). Refused (409) while a run is in flight.
 */
export function askDialogueAgain(bookID: string): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/dialogue`, {
    method: "POST",
    body: JSON.stringify({ again: true }),
  });
}

/** findLines asks again, for a character added since, the lines given to someone else. */
export function findLines(bookID: string, characterID: string): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/${characterID}/dialogue`, {
    method: "POST",
  });
}

export interface CharacterEdit {
  name: string;
  aliases: string[];
  role: string;
  description: string;
  introduction: string;
}

/** getCharacters is the studio's working roster with its run and estimate (ADR-0034). */
export function getCharacters(bookID: string): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters?draft=true`);
}

/**
 * A character as a reader is shown it (requirement 0011): who they are when
 * the book introduces them. The description and the chapters they appear in
 * tell the story ahead, so they come only with spoilers.
 */
export interface ReaderCharacter {
  id: string;
  name: string;
  aliases: string[];
  role: string;
  introduction: string;
  description?: string;
  appearances?: CharacterAppearance[];
  /**
   * The face, from the published edition's own columns (ADR-0077). Absent for
   * a character with none, and for a book published before their portrait was
   * drawn — a redraw reaches readers at the next publish.
   */
  portrait?: { url: string; width: number; height: number };
}

export interface ReaderCast {
  characters: ReaderCharacter[];
  /** How many the reader has still to meet. */
  unmet_count: number;
  spoilers: boolean;
}

/**
 * getReaderCast is the published edition's cast as far as the signed-in
 * reader has listened — or everyone, with their arcs, when `spoilers` is
 * asked for. Costs nothing.
 */
export function getReaderCast(bookID: string, spoilers = false): Promise<ReaderCast> {
  const query = spoilers ? "?spoilers=1" : "";
  return request<ReaderCast>(`/api/v1/books/${bookID}/characters${query}`);
}

/** getNamedCharacters is the cast a line names, for a long press. Costs nothing. */
export function getNamedCharacters(chapterID: string, line: string): Promise<ReaderCast> {
  return request<ReaderCast>(
    `/api/v1/chapters/${chapterID}/characters?line=${encodeURIComponent(line)}`,
  );
}

export interface CharacterRecap {
  text: string;
  cost_micros: number;
  cost_display: string;
  remaining_this_hour: number;
  /** Somebody already paid for this point in the book; this one was free. */
  cached: boolean;
}

/**
 * recapCharacter is "what do I know about them so far?" for one character, at
 * the line the reader pressed. **Spends**, from the reader's own library like
 * a question (ADR-0013) — so it fires only on a tap, and a visitor gets a 401.
 */
export function recapCharacter(
  chapterID: string,
  characterID: string,
  line: string,
): Promise<CharacterRecap> {
  return request<CharacterRecap>(`/api/v1/chapters/${chapterID}/characters/${characterID}/recap`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ line }),
  });
}

/**
 * extractCharacters starts a run: every chapter not yet read is read by the
 * worker, then one merge compiles the roster. **Spends.** A run already going
 * is returned rather than doubled.
 */
export function extractCharacters(bookID: string): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/extract`, {
    method: "POST",
  });
}

export function addCharacter(bookID: string, edit: CharacterEdit): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(edit),
  });
}

/** A suggestion for a character's fields, read from the book (ADR-0054). */
export interface CharacterFill {
  aliases: string[];
  role: string;
  introduction: string;
  description: string;
  /** How many places in the book it was read from. */
  passages: number;
  cost_micros: number;
  cost_display: string;
}

/** fillCharacter asks the model who a named character is. Saves nothing. */
export function fillCharacter(
  bookID: string,
  who: { name: string; aliases: string[] },
): Promise<CharacterFill> {
  return request<CharacterFill>(`/api/v1/books/${bookID}/characters/fill`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(who),
  });
}

export function updateCharacter(
  bookID: string,
  characterID: string,
  edit: CharacterEdit,
): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/${characterID}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(edit),
  });
}

/**
 * setNarrator makes a character the book's narrator, clearing any other, or
 * stops it being one (ADR-0137). Not an edit of their words.
 */
export function setNarrator(
  bookID: string,
  characterID: string,
  narrates: boolean,
): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/${characterID}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ narrates }),
  });
}

/**
 * setSpeaksAlone makes the book one speaker, this character, and so its
 * narrator, or makes it an ordinary book again (ADR-0180). Refused while who
 * says each line is being found.
 */
export function setSpeaksAlone(
  bookID: string,
  characterID: string,
  speaksAlone: boolean,
): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/${characterID}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ speaks_alone: speaksAlone }),
  });
}

export function deleteCharacter(bookID: string, characterID: string): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/${characterID}`, {
    method: "DELETE",
  });
}

/** mergeCharacter folds one character into another, which keeps its words. */
/**
 * One turn of the conversation about how a character looks. A fraction of a
 * cent; drawing the portrait is the expensive step and a separate call.
 */
export function describePortrait(
  bookID: string,
  characterID: string,
  body: { current?: string; earlier?: string[]; instruction: string },
  /** One of the character's forms, described as that form (ADR-0159). */
  formID?: string,
): Promise<{ text: string; cost_micros: number }> {
  return request(`${formsPath(bookID, characterID, formID)}/portrait/describe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Draws a portrait. Spends on every call; it is not the character's until set. */
export function generatePortrait(
  bookID: string,
  characterID: string,
  prompt: string,
  quality?: ImageQuality,
): Promise<ImageCandidate> {
  return request<ImageCandidate>(
    `/api/v1/books/${bookID}/characters/${characterID}/portrait/generate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, quality }),
    },
  );
}

/** Uploads a portrait. The body is the file; the server reads its format. */
export function uploadPortrait(
  bookID: string,
  characterID: string,
  file: Blob,
): Promise<ImageCandidate> {
  return request<ImageCandidate>(
    `/api/v1/books/${bookID}/characters/${characterID}/portrait/upload`,
    { method: "POST", body: file },
  );
}

/** Crops a stored picture into a square portrait candidate. */
export function cropPortrait(
  bookID: string,
  characterID: string,
  objectKey: string,
  region: CropRegion,
): Promise<ImageCandidate> {
  return cropAt(
    `/api/v1/books/${bookID}/characters/${characterID}/portrait/crop`,
    objectKey,
    region,
  );
}

/**
 * Draws the chosen characters' faces in the background (ADR-0049), and
 * returns the roster with them pending.
 */
export function queuePortraits(bookID: string, characterIDs: string[]): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/portraits/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ character_ids: characterIDs }),
  });
}

/** Queues live portraits for characters with portraits (ADR-0127). */
export function queueLivePortraits(bookID: string, characterIDs: string[]): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/portraits/live`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ character_ids: characterIDs }),
  });
}

/**
 * One picture of a live portrait from this origin, for the studio's WebGL
 * canvas. The content key in the query makes a changed portrait a new URL.
 */
export function livePictureURL(
  bookID: string,
  characterID: string,
  part: "image" | "blink" | "plate" | "depth",
  key: string,
): string {
  return `/api/v1/books/${bookID}/characters/${characterID}/live/${part}?k=${encodeURIComponent(key)}`;
}

/** Makes a stored picture the character's reference, and returns the roster. */
export function setPortrait(
  bookID: string,
  characterID: string,
  body: {
    object_key: string;
    prompt?: string;
    source: "upload" | "generated";
    quality?: ImageQuality;
  },
  /** One of the character's forms, whose reference this becomes (ADR-0159). */
  formID?: string,
): Promise<Characters> {
  return request<Characters>(`${formsPath(bookID, characterID, formID)}/portrait`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Cover is a book's picture (ADR-0041). */
export interface Cover {
  url: string;
  object_key: string;
  width: number;
  height: number;
  /** The description a generated cover was drawn from; absent on an upload. */
  prompt?: string;
  source: "upload" | "generated";
  /** Who it was drawn from, still on the roster, by id and by name (ADR-0053). */
  character_ids: string[];
  drawn_from: string[];
}

export interface CoverStatus {
  cover?: Cover;
  /** The roster's names, so the studio can say who may be put on the cover. */
  characters: string[];
  cast: Cast;
  /** What drawing one costs at each quality offered (ADR-0086). */
  draw_prices: DrawPrice[];
  /** The aspect ratio a cover is drawn in, so the box can be reserved. */
  shape: string;
}

/** A book's picture medium (ADR-0168): the few words every scene and cover is drawn in. */
export interface PictureMedium {
  /** Empty when none has been chosen: the first picture then chooses. */
  medium: string;
  /** The bound, in characters. */
  max_chars: number;
}

export function getPictureMedium(bookID: string): Promise<PictureMedium> {
  return request<PictureMedium>(`/api/v1/books/${bookID}/picture-medium`);
}

/** setPictureMedium sets, changes or — with "" — clears it. Free: it draws nothing. */
export function setPictureMedium(bookID: string, medium: string): Promise<PictureMedium> {
  return request<PictureMedium>(`/api/v1/books/${bookID}/picture-medium`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ medium }),
  });
}

export function getCover(bookID: string): Promise<CoverStatus> {
  return request<CoverStatus>(`/api/v1/books/${bookID}/cover`);
}

export function describeCover(
  bookID: string,
  body: { current?: string; character_ids?: string[]; earlier?: string[]; instruction: string },
): Promise<PictureDescription> {
  return request(`/api/v1/books/${bookID}/cover/describe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function generateCover(
  bookID: string,
  body: { prompt: string; character_ids: string[]; quality?: ImageQuality },
): Promise<ImageCandidate> {
  return request<ImageCandidate>(`/api/v1/books/${bookID}/cover/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Uploads a cover. The body is the file; the server reads its format. */
export function uploadCover(bookID: string, file: Blob): Promise<ImageCandidate> {
  return request<ImageCandidate>(`/api/v1/books/${bookID}/cover/upload`, {
    method: "POST",
    body: file,
  });
}

/** Crops a stored picture into an upright cover candidate. */
export function cropCover(
  bookID: string,
  objectKey: string,
  region: CropRegion,
): Promise<ImageCandidate> {
  return cropAt(`/api/v1/books/${bookID}/cover/crop`, objectKey, region);
}

export function setCover(
  bookID: string,
  body: {
    object_key: string;
    prompt?: string;
    source: "upload" | "generated";
    quality?: ImageQuality;
    character_ids?: string[];
  },
): Promise<CoverStatus> {
  return request<CoverStatus>(`/api/v1/books/${bookID}/cover`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function clearCover(bookID: string): Promise<CoverStatus> {
  return request<CoverStatus>(`/api/v1/books/${bookID}/cover`, {
    method: "DELETE",
  });
}

export function clearPortrait(
  bookID: string,
  characterID: string,
  formID?: string,
): Promise<Characters> {
  return request<Characters>(`${formsPath(bookID, characterID, formID)}/portrait`, {
    method: "DELETE",
  });
}

export function mergeCharacter(
  bookID: string,
  characterID: string,
  into: string,
): Promise<Characters> {
  return request<Characters>(`/api/v1/books/${bookID}/characters/${characterID}/merge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ into }),
  });
}

/** What "Narrate all" would do to a chapter (ADR-0119). */
export type BookGroupKind =
  "not_narrated" | "text_changed" | "refinish" | "revoiced" | "other_settings";

/** A setting a chapter was recorded on differently from the book's now. */
export type SettingDiff =
  "model" | "voice" | "part_size" | "read_context" | "direction" | "language";

/** A set of chapters "Narrate all" treats alike. */
export interface BookGroup {
  /** Opaque; names an other_settings or revoiced group in the confirmation. */
  key: string;
  kind: BookGroupKind;
  chapters: number;
  estimate_micros: number;
  estimate_display: string;
  /** What an other_settings group was recorded on. */
  model?: string;
  model_label?: string;
  voice?: string;
  voice_label?: string;
  differs?: SettingDiff[];
}

/** BookNarration is what "Narrate all" would do, in groups. */
export interface BookNarration {
  /** Every group's chapters and price: a confirmation with every group ticked. */
  chapters: number;
  /** Chapters already being narrated, which are left alone. */
  narrating: number;
  estimate_micros: number;
  estimate_display: string;
  /** The book's settings now, which every group moves to. */
  model_label: string;
  voice_label: string;
  groups: BookGroup[];
}

export type BookRunStatus = "running" | "done" | "stopped" | "failed";

/** BookRun is one press of "Narrate all", as its job has got through it. */
export interface BookRun {
  id: string;
  status: BookRunStatus;
  /** Why it stopped: budget, no_budget, failed, timed_out. */
  reason?: string;
  planned: number;
  done: number;
  started: number;
  already_narrating: number;
  not_started: number;
  created_at: string;
}

export function getBookNarrationEstimate(
  bookID: string,
  mode: RenderMode = "fast",
): Promise<BookNarration> {
  return request<BookNarration>(`/api/v1/books/${bookID}/renders/estimate?mode=${mode}`);
}

/**
 * narrateBook confirms "Narrate all": every chapter with no audio or changed
 * text, and the other-settings and same-text groups named in `include`. **Spends**, through a
 * job; the run says how far it got. 409 when the book changed since the estimate.
 */
export function narrateBook(
  bookID: string,
  include: string[],
  mode: RenderMode = "fast",
): Promise<BookRun> {
  return request<BookRun>(`/api/v1/books/${bookID}/renders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode, include }),
  });
}

/** A passage of a book with no audio, as an author finds and fixes it (ADR-0120). */
export interface NarrationProblem {
  chapter_id: string;
  chapter_title: string;
  render_status: "partial" | "failed";
  part_id: string;
  text: string;
  failure: PartFailure;
  unsaid_start?: number;
  unsaid_end?: number;
  message?: string;
  retryable: boolean;
}

/** Every passage of the book with no audio, in reading order. */
export async function getBookProblems(bookID: string): Promise<NarrationProblem[]> {
  const out = await request<{ items: NarrationProblem[] }>(
    `/api/v1/books/${bookID}/renders/problems`,
  );
  return out.items;
}

/** The book's latest "Narrate all", or undefined when there has never been one. */
export function getBookNarrationRun(bookID: string): Promise<BookRun | undefined> {
  return request<BookRun | undefined>(`/api/v1/books/${bookID}/renders/run`);
}

/** translationEstimate prices translating a book into a language (requirement 0018). */
export function translationEstimate(
  bookID: string,
  language: string,
  instruction = "",
  preset = "",
  effort = "",
): Promise<TranslationEstimate> {
  // The instruction rides every call, so it is priced: an estimate that ignored
  // it would under-authorize the run it exists to warn about. The effort is
  // priced for the same reason — thinking bills as output (ADR-0097).
  const query = new URLSearchParams({ language, instruction, preset, effort });
  return request<TranslationEstimate>(
    `/api/v1/books/${encodeURIComponent(bookID)}/translations/estimate?${query}`,
  );
}

/**
 * startTranslation creates the translated book and starts its run; it answers
 * with the new book.
 *
 * `linked` asks for a book that stays derived from its source and is brought up
 * to date by Refresh, rather than a copy the author owns (ADR-0093).
 */
export function startTranslation(order: TranslationOrder): Promise<Book> {
  const { bookID, ...body } = order;
  return request<Book>(`/api/v1/books/${encodeURIComponent(bookID)}/translations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** TranslationOrder is where a book is going and how it should read. */
export interface TranslationOrder {
  bookID: string;
  language: string;
  /** The author's words, empty for the default (ADR-0094). */
  instruction?: string;
  /** The preset it came from; the server decides whether the label still holds. */
  preset?: string;
  linked?: boolean;
  /** How hard to think about it, absent for the server's default (ADR-0097). */
  effort?: Effort;
}

/** A ready-made destination: a name, a real language code, and its instruction. */
export interface TranslationPreset {
  id: string;
  name: string;
  language: string;
  instruction: string;
}

/** TranslationStyle is what the step before Start shows. */
export interface TranslationStyle {
  presets: TranslationPreset[];
  /**
   * The rules that always go with the instruction, in the words they are sent
   * in. Shown read-only and never translated: translating them for display
   * would be showing something other than what is sent (ADR-0094).
   */
  rules: string[];
  max_runes: number;
  default_instruction: string;
  /** The thinking efforts on offer, least first, and the one a press with none runs at. */
  efforts: Effort[];
  default_effort: Effort;
}

/** Sample is one paragraph tried under the instruction as it stands. */
export interface Sample {
  source: string;
  translated: string;
  chapter_title: string;
  /**
   * Which paragraph this was. Send it back on the next press to hold it still,
   * or with `different` to move off it (ADR-0099). Positional and ephemeral:
   * never store it, and one the server cannot resolve falls back to the
   * deterministic paragraph rather than failing.
   */
  paragraph: ParagraphRef;
  cost_micros: number;
  cost: string;
}

/** ParagraphRef names a paragraph of the source: its chapter and its index. */
export interface ParagraphRef {
  chapter_id: string;
  index: number;
}

/**
 * trySample translates one paragraph so the author can see what an instruction
 * does before paying for the run.
 *
 * **Only ever call this from a press.** Never on a keystroke, on choosing a
 * preset, or on opening the screen: that rule is what stands in for an hourly
 * limit on a paid call (ADR-0095), and an e2e spec asserts it. Two press
 * controls are still two presses, and each says what it spends (ADR-0099).
 *
 * `paragraph` holds the one on screen still, so a second press compares two
 * instructions rather than two paragraphs; `different` asks for another one,
 * translated in the same press. Neither is the first press, which takes the
 * first paragraph of the book with enough text.
 */
export function trySample(
  order: Omit<TranslationOrder, "linked"> & { paragraph?: ParagraphRef; different?: boolean },
): Promise<Sample> {
  const { bookID, ...body } = order;
  return request<Sample>(`/api/v1/books/${encodeURIComponent(bookID)}/translations/sample`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/**
 * translationStyle is the step's own data, for a destination language.
 *
 * `ownLanguageOnly` filters the presets to that language, which is what a linked
 * book's settings needs: its destination was settled at Start, so a preset in
 * another language is offering a different book (ADR-0096).
 */
export function translationStyle(
  language: string,
  ownLanguageOnly = false,
): Promise<TranslationStyle> {
  const query = new URLSearchParams({ language });
  if (ownLanguageOnly) query.set("only", "language");
  return request<TranslationStyle>(`/api/v1/translations/style?${query}`);
}

/**
 * setInstruction changes what the next refresh of a linked book translates
 * with, and answers with what that refresh would now do.
 *
 * It starts nothing: saving is free and reversible, refreshing is neither
 * (ADR-0096).
 */
export function setInstruction(
  bookID: string,
  instruction: string,
  preset = "",
  effort?: Effort,
): Promise<Refresh> {
  return request<Refresh>(`/api/v1/books/${encodeURIComponent(bookID)}/translation/instruction`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    // The effort is not stored by this press — only a press that starts a pass
    // records one (ADR-0097 §1). It travels so the preview that comes back is
    // priced at the chip the author is looking at rather than at the default.
    body: JSON.stringify({ instruction, preset, effort: effort ?? "" }),
  });
}

/** Refresh is what refreshing a linked book would do, before it spends anything. */
export interface Refresh {
  details: boolean;
  chapters_changed: number;
  chapters_added: number;
  chapters_removed: number;
  pictures_changed: number;
  estimate_micros: number;
  estimate: string;
  /**
   * How many chapters will lose a recording that is good now (ADR-0096).
   * What the refresh destroys, beside what it spends — re-translating writes a
   * new revision and narration binds to a revision.
   */
  audio_outdated: number;
  chapters_edited: number;
  /**
   * Why the plan is what it is, when the counts alone would mislead:
   * "instruction" when the author changed it, absent otherwise. The server says
   * it because the server is the only thing that knows.
   */
  cause?: string;
  /** Whether there is anything to do at all — the server's own sum. */
  any: boolean;
}

/**
 * refreshPreview asks what a refresh would change. It spends nothing.
 *
 * The effort is the one the chip is sitting on, not the one the last pass ran
 * at: the preview prices the press the author is about to make (ADR-0097 §6).
 */
export function refreshPreview(bookID: string, effort = ""): Promise<Refresh> {
  const query = effort ? `?${new URLSearchParams({ effort })}` : "";
  return request<Refresh>(
    `/api/v1/books/${encodeURIComponent(bookID)}/translation/refresh${query}`,
  );
}

/**
 * The conflict inbox (ADR-0098 §8): what a refresh kept of the author's words,
 * and what it would have written instead.
 */
export interface DiffRun {
  text: string;
  /** "same" | "removed" | "added". */
  kind: string;
}

export interface Conflict {
  id: string;
  /** "paragraph" | "chapter" | "title". */
  unit: string;
  /** The author's wording as the book says it **now**, read live. */
  kept: string;
  proposed: string;
  /**
   * What moved in the source, word by word. Computed on the server because
   * `\b` is ASCII-only in JavaScript and fires inside Arabic-script words
   * (ADR-0098 §8); the client only renders these runs.
   */
  source_diff: DiffRun[];
  /** Its paragraph is no longer in the chapter, so it cannot be taken. */
  superseded: boolean;
}

export interface ConflictChapter {
  chapter_id: string;
  title: string;
  audio_outdated: boolean;
  items: Conflict[];
}

export interface Inbox {
  open: number;
  chapters: ConflictChapter[];
}

export function listConflicts(bookID: string): Promise<Inbox> {
  return request<Inbox>(`/api/v1/books/${encodeURIComponent(bookID)}/translation/conflicts`);
}

/** resolveConflict settles one item: take writes the alternative, keep does not. */
export function resolveConflict(conflictID: string, take: boolean): Promise<void> {
  return request<void>(`/api/v1/conflicts/${encodeURIComponent(conflictID)}/resolve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ take }),
  });
}

/**
 * resolveConflicts settles a whole chapter, or the whole book when chapterID is
 * omitted. Take-all writes one revision per chapter, not one per paragraph.
 */
export function resolveConflicts(
  bookID: string,
  take: boolean,
  chapterID?: string,
): Promise<{ settled: number }> {
  return request<{ settled: number }>(
    `/api/v1/books/${encodeURIComponent(bookID)}/translation/conflicts/resolve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ take, chapter_id: chapterID ?? "" }),
    },
  );
}

/** refreshTranslation re-transforms what the source has changed since the last pass. */
export function refreshTranslation(bookID: string, effort?: Effort): Promise<Translation> {
  return request<Translation>(`/api/v1/books/${encodeURIComponent(bookID)}/translation/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ effort: effort ?? "" }),
  });
}

/** unlinkBook turns a linked book into an ordinary one. Final: there is no relink. */
export function unlinkBook(bookID: string): Promise<Book> {
  return request<Book>(`/api/v1/books/${encodeURIComponent(bookID)}/unlink`, { method: "POST" });
}

/** getTranslation is a translated book's run, or null for a book that is not one. */
export async function getTranslation(bookID: string): Promise<Translation | null> {
  try {
    return await request<Translation>(`/api/v1/books/${encodeURIComponent(bookID)}/translation`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function retryTranslation(bookID: string): Promise<Translation> {
  return request<Translation>(`/api/v1/books/${encodeURIComponent(bookID)}/translation/retry`, {
    method: "POST",
  });
}

export function cancelTranslation(bookID: string): Promise<Translation> {
  return request<Translation>(`/api/v1/books/${encodeURIComponent(bookID)}/translation/cancel`, {
    method: "POST",
  });
}

/**
 * A book's cast of voices (ADR-0157, requirement 0090): the narrator and each
 * character with lines, each designed from a description and chosen by ear.
 */
export interface DesignedVoice {
  id: string;
  description: string;
  /** Our clip of the role's lines on this voice; "" when none was bought (ADR-0172). */
  sample_url: string;
  sample_ms: number;
  /** The vendor's own free clip of an account or library voice; "" when it has none. */
  preview_url: string;
  /** The saved voice, present once chosen. */
  vendor_voice_id?: string;
  /** "account" for a voice the vendor account already held, named by label. */
  source: "designed" | "account" | "library";
  label?: string;
}

export interface CastRole {
  /** Null for the narrator, an extra and a form. */
  character_id: string | null;
  /** An extra voice's kind (ADR-0157 §9); absent for the narrator and a character. */
  extra?: ExtraKind;
  /** A character form's role (ADR-0159), and whose form it is. */
  form_id?: string;
  of_character_id?: string;
  name: string;
  role: string;
  lines: number;
  description: string;
  /** What candidates read: the role's own lines, or a narration paragraph. */
  sample_text: string;
  /** What one design costs. */
  estimate_micros: number;
  /** What the sample press costs: the role's lines spoken once on its cast voice. Casting is free. */
  sample_micros: number;
  voice: DesignedVoice | null;
  candidates: DesignedVoice[];
}

export interface Cast {
  book_id: string;
  offered: boolean;
  reason?: string;
  model: string;
  /** The book's voice accent and the set it may take (ADR-0164); absent when voices are not offered. */
  accent?: string;
  accents?: string[];
  roles: CastRole[];
}

export function castKey(bookID: string): string[] {
  return ["cast", bookID];
}

export function getCast(bookID: string): Promise<Cast> {
  return request<Cast>(`/api/v1/books/${bookID}/cast`);
}

/** RoleRef names a role in a cast request: a character, an extra kind, or neither for the narrator. */
export interface RoleRef {
  character_id: string | null;
  extra?: ExtraKind;
  form_id?: string;
}

/** designVoices designs two candidates for one role. It spends. */
export function designVoices(
  bookID: string,
  role: RoleRef,
  description: string,
): Promise<CastRole> {
  return request<CastRole>(`/api/v1/books/${bookID}/cast/design`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      character_id: role.character_id,
      extra: role.extra ?? "",
      form_id: role.form_id,
      description,
    }),
  });
}

/** The voices the vendor account already holds, which a role may take instead of a design. */
export interface AccountVoices {
  /** False when the voice service would not list them: a voice id is typed instead. */
  listed: boolean;
  reason?: string;
  voices: { voice_id: string; name: string }[];
  slots_used: number;
  slots_limit: number;
}

export function accountVoicesKey(bookID: string): string[] {
  return ["account-voices", bookID];
}

export function getAccountVoices(bookID: string): Promise<AccountVoices> {
  return request<AccountVoices>(`/api/v1/books/${bookID}/cast/account-voices`);
}

/** castAccountVoice casts a voice the account already holds. It spends nothing (ADR-0172). */
export function castAccountVoice(
  bookID: string,
  role: RoleRef,
  voiceID: string,
  label: string,
): Promise<CastRole> {
  return request<CastRole>(`/api/v1/books/${bookID}/cast/use`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      character_id: role.character_id,
      extra: role.extra ?? "",
      form_id: role.form_id,
      voice_id: voiceID,
      label,
    }),
  });
}

/** A voice in the vendor's public library (requirement 0090's amendment). Its preview is free. */
export interface LibraryVoice {
  voice_id: string;
  owner_id: string;
  name: string;
  description: string;
  preview_url: string;
  gender: string;
  age: string;
  accent: string;
  use_case: string;
  /** How long its owner must give before withdrawing it. */
  notice_days: number;
  /** Already in the account: choosing it adds nothing. */
  in_account: boolean;
  /** The vendor's quality tier; "high_quality" is its HQ badge. */
  category?: string;
  /** Characters spoken on the voice in the last year, by everyone. */
  usage_year?: number;
  /** What the voice sounds like, heard once for every library (ADR-0198); null until heard. */
  notes?: VoiceNotes | null;
}

/** A model's notes on a library voice's preview (ADR-0198), in English. */
export interface VoiceNotes {
  age_low: number;
  age_high: number;
  gender: string;
  accent: string;
  energy: string;
  character: string;
  model: string;
  heard_at: string;
}

export interface LibraryMatches {
  /** The strictest search asked, built from the description. */
  search: { gender: string; age: string; accent: string; use_case: string; words: string[] };
  /** True when matches came only after dropping some of the search. */
  relaxed: boolean;
  voices: LibraryVoice[];
  /** The account's monthly voice adds, which choosing takes one of; 0 when unread. A library voice takes no voice slot. */
  add_edits_used: number;
  add_edits_limit: number;
  /** How many matches a find-and-hear press would hear, and its price (ADR-0198). */
  hear_count?: number;
  hear_micros?: number;
  /** On a find-and-hear press: how many it heard, and the voice ids it could not. */
  heard?: number;
  unheard?: string[];
}

/** findLibraryVoices searches the library from a role's description. Free. */
export function findLibraryVoices(
  bookID: string,
  role: RoleRef,
  description: string,
): Promise<LibraryMatches> {
  const q = new URLSearchParams();
  if (role.character_id) q.set("character_id", role.character_id);
  if (role.extra) q.set("extra", role.extra);
  if (role.form_id) q.set("form_id", role.form_id);
  if (description) q.set("description", description);
  return request<LibraryMatches>(`/api/v1/books/${bookID}/cast/library?${q.toString()}`);
}

/**
 * sampleVoice speaks a role's lines once on its cast voice (ADR-0172). It
 * spends, once: a voice that already has a sample gets it back free.
 */
export function sampleVoice(bookID: string, role: RoleRef): Promise<CastRole> {
  return request<CastRole>(`/api/v1/books/${bookID}/cast/sample`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      character_id: role.character_id,
      extra: role.extra ?? "",
      form_id: role.form_id,
    }),
  });
}

/** castLibraryVoice adds a library voice to the account (no voice slot, one of the month's voice adds) and casts it. It spends nothing (ADR-0172). */
export function castLibraryVoice(
  bookID: string,
  role: RoleRef,
  voice: Pick<LibraryVoice, "owner_id" | "voice_id" | "name">,
): Promise<CastRole> {
  return request<CastRole>(`/api/v1/books/${bookID}/cast/library/use`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      character_id: role.character_id,
      extra: role.extra ?? "",
      form_id: role.form_id,
      owner_id: voice.owner_id,
      voice_id: voice.voice_id,
      label: voice.name,
    }),
  });
}

/** chooseVoice saves a candidate and casts it; the narrator's becomes the book's voice. */
export function chooseVoice(bookID: string, candidateID: string): Promise<CastRole> {
  return request<CastRole>(`/api/v1/books/${bookID}/cast/choose`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ candidate_id: candidateID }),
  });
}

/**
 * A curated collection as a reader sees it (requirement 0119, ADR-0185).
 * `count` and the cover are over the books this viewer may read; the page
 * carries those books, in the admin's order.
 */
export interface Collection {
  id: string;
  name: string;
  description?: string;
  kind: "collection" | "series";
  count: number;
  cover_url?: string;
  cover_width?: number;
  cover_height?: number;
  books?: Book[];
}

/**
 * A volume's place in its series: "Vol. number of of · name". Numbered among
 * the volumes this viewer may read, so a hidden one leaves no gap.
 */
export interface SeriesPlace {
  id: string;
  name: string;
  number: number;
  of: number;
  previous?: { id: string; title: string };
  next?: { id: string; title: string };
}

/** Browse's collection row: only collections holding a book this viewer may read. */
export function listCollections(): Promise<{ items: Collection[] }> {
  return request<{ items: Collection[] }>("/api/v1/collections");
}

export function getCollection(id: string): Promise<Collection> {
  return request<Collection>(`/api/v1/collections/${id}`);
}

/** A linked book as curation sees it, drafts and private books included. */
export interface CuratedBook {
  id: string;
  title: string;
  author?: string;
  visibility: string;
  published: boolean;
}

export interface CuratedCollection {
  id: string;
  name: string;
  description: string;
  kind: "collection" | "series";
  updated_at: string;
  books: CuratedBook[];
}

/** A tag a book carries (requirement 0119 T3): one name, shared by every library. */
export interface Tag {
  id: string;
  name: string;
}

/** A tag to set on a book: an existing one by id, or a name to find or make. */
export type TagChoice = { id: string } | { name: string };

/** Browse's chips: the tags of the books this viewer may read there. */
export function browseTags(): Promise<{ items: Tag[] }> {
  return request<{ items: Tag[] }>("/api/v1/tags");
}

/** The studio's tag search for a book: tags its library can see. */
export function tagOptions(bookID: string, q: string): Promise<{ items: Tag[] }> {
  return request(`/api/v1/books/${bookID}/tag-options?q=${encodeURIComponent(q)}`);
}

/** A tag in the admin's list, with how many live books carry it. */
export interface CuratedTag extends Tag {
  books: number;
}

/** Every tag. Platform admin only. */
export function listCuratedTags(): Promise<{ items: CuratedTag[] }> {
  return request<{ items: CuratedTag[] }>("/api/v1/admin/tags");
}

export function renameTag(id: string, name: string): Promise<Tag> {
  return request<Tag>(`/api/v1/admin/tags/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}

/** Moves every book carrying `id` onto `into`, and deletes `id`. */
export function mergeTag(id: string, into: string): Promise<void> {
  return request<void>(`/api/v1/admin/tags/${id}/merge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ into }),
  });
}

export function deleteTag(id: string): Promise<void> {
  return request<void>(`/api/v1/admin/tags/${id}`, { method: "DELETE" });
}

/** Every collection with every book linked. Platform admin only. */
export function listCuratedCollections(): Promise<{ items: CuratedCollection[] }> {
  return request<{ items: CuratedCollection[] }>("/api/v1/admin/collections");
}

export function createCollection(name: string, description: string): Promise<CuratedCollection> {
  return request<CuratedCollection>("/api/v1/admin/collections", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, description }),
  });
}

export function updateCollection(
  id: string,
  patch: { name?: string; description?: string; kind?: "collection" | "series" },
): Promise<CuratedCollection> {
  return request<CuratedCollection>(`/api/v1/admin/collections/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export function deleteCollection(id: string): Promise<void> {
  return request<void>(`/api/v1/admin/collections/${id}`, { method: "DELETE" });
}

/** setCollectionBooks makes these exactly the collection's books, in this order. */
export function setCollectionBooks(id: string, bookIDs: string[]): Promise<CuratedCollection> {
  return request<CuratedCollection>(`/api/v1/admin/collections/${id}/books`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ book_ids: bookIDs }),
  });
}

/**
 * What this platform is told (ADR-0192). On the web, `install_urls` is each
 * app's install link — keyed `ios` and `android`, absent when unset — for the
 * install bar (ADR-0209).
 */
export type ClientConfig = {
  platform: string;
  update: string;
  plus_gate: boolean;
  install_urls?: Partial<Record<"ios" | "android", string>>;
};

export function getClientConfig(): Promise<ClientConfig> {
  return request<ClientConfig>("/api/v1/client-config");
}
